/**
 * FreeformCanvas.js - P0-TB1: Editor Freeform de Templates
 * 
 * Canvas A4 com react-konva:
 * - Zoom 50%-200%
 * - Drag/resize elementos
 * - 5 tipos de elementos: Texto, Variável, Imagem, Linha/Rect, Tabela
 * - Persistência JSON
 * - Pré-visualização PDF real
 * - P0-TB-GRID-FIX-01: Correção Grid X/Y e Seleção Estável
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Stage, Layer, Rect, Text, Line, Image as KonvaImage, Transformer, Group } from 'react-konva';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { buildAuthHeaders } from '@/lib/authStorage';
import { toast } from 'sonner';
import { CatalogContractBadges } from '@/components/template-builder/CatalogContractBadges';
import {
  FALLBACK_TABLE_DATASOURCES,
  FALLBACK_VARIABLE_GROUPS,
  buildTableDatasourcesFromCatalog,
  buildVariableOptionsFromCatalog,
  cloneDatasourceColumns,
  flattenVariableOptions,
  getElementDisplayLabel,
  getTableDatasourceMeta,
} from '@/lib/templateCatalogAdapter';
import {
  ZoomIn, ZoomOut, Trash2, Type, Image, Square, Table2, 
  Variable, Save, Eye, Minus, Bold, Italic,
  AlignLeft, AlignCenter, AlignRight, Undo2, Redo2,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Layers, Upload, Lock, Unlock, LayoutGrid, Landmark, AlertTriangle
} from 'lucide-react';

// Max image size in bytes (500KB)
const MAX_IMAGE_SIZE = 500 * 1024;
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compress image to JPEG with quality control
const compressImage = (file, maxSize = MAX_IMAGE_SIZE) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        // If already small enough and is JPEG/PNG, use as-is
        if (file.size <= maxSize) {
          resolve({ dataUrl: e.target.result, width: img.width, height: img.height });
          return;
        }
        
        // Compress by reducing quality
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        // Try different quality levels
        let quality = 0.8;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        while (dataUrl.length > maxSize * 1.37 && quality > 0.1) { // base64 is ~37% larger
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        
        // If still too large, resize
        if (dataUrl.length > maxSize * 1.37) {
          const scale = Math.sqrt((maxSize * 1.37) / dataUrl.length);
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        }
        
        resolve({ dataUrl, width: img.width, height: img.height });
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// A4 dimensions in points (1 point = 1/72 inch)
const A4_WIDTH = 595;
const A4_HEIGHT = 842;
const CANVAS_PADDING = 40;
// FIX: 1mm = 2.83465pt
const MM_TO_PT = 2.83465;

const DEFAULT_GRID_SETTINGS = {
  enabled: false,
  spacingMm: 5,
  snap: false,
};

const COMPANY_BANK_PREVIEW_ROWS = [
  {
    label: 'BCI',
    bank_name: 'Banco Comercial e de Investimentos',
    bank_account_holder: 'CG Business, Lda.',
    bank_account_number: '000123456789',
    bank_iban: 'MZ59000123456789012345678',
    bank_swift: 'BCOMMZMA',
  },
  {
    label: 'Millennium BIM',
    bank_name: 'Banco Internacional de Moçambique',
    bank_account_holder: 'CG Business, Lda.',
    bank_account_number: '009876543210',
    bank_iban: 'MZ59987654321000987654321',
    bank_swift: 'BIMOMZMA',
  },
];

const roundTo2 = (value) => Math.round(value * 100) / 100;

const snapToGrid = (value, spacingPt) => {
  if (!spacingPt) return value;
  return Math.round(value / spacingPt) * spacingPt;
};

const resequenceZIndexes = (items) => items.map((item, index) => ({
  ...item,
  zIndex: index,
}));

const deepMergeLayout = (baseValue, patchValue) => {
  if (Array.isArray(patchValue)) {
    return patchValue;
  }

  if (
    baseValue
    && patchValue
    && typeof baseValue === 'object'
    && typeof patchValue === 'object'
    && !Array.isArray(baseValue)
  ) {
    const next = { ...baseValue };
    Object.entries(patchValue).forEach(([key, value]) => {
      next[key] = key in baseValue ? deepMergeLayout(baseValue[key], value) : value;
    });
    return next;
  }

  return patchValue;
};

const mergeElementsByIdentity = (baseElements = [], patchElements = []) => {
  const baseMap = new Map(baseElements.map((element) => [element.id, element]));
  const touchedIds = new Set();

  const mergedBase = baseElements.map((element) => {
    const patch = patchElements.find((candidate) => candidate?.id && candidate.id === element.id);
    if (!patch) return element;
    touchedIds.add(element.id);
    return deepMergeLayout(element, patch);
  });

  const appended = patchElements
    .filter((element) => element && typeof element === 'object')
    .filter((element) => !element.id || (!baseMap.has(element.id) && !touchedIds.has(element.id)));

  return [...mergedBase, ...appended];
};

const REFERENCE_PRESERVED_KEYS = [
  'id',
  'type',
  'text',
  'variablePath',
  'datasource',
  'columns',
  'imageUrl',
  'dataUrl',
  'src',
];

const mergeElementPropertiesFromReference = (currentElement, referenceElement) => {
  const nextElement = {
    ...currentElement,
    ...referenceElement,
  };

  REFERENCE_PRESERVED_KEYS.forEach((key) => {
    if (key in currentElement) {
      nextElement[key] = currentElement[key];
    } else {
      delete nextElement[key];
    }
  });

  return nextElement;
};

export const getTemplateDraftStorageKey = (templateId) => `cg-template-builder-draft:${templateId}`;

export const readTemplateDraft = (templateId) => {
  if (!templateId) return null;
  try {
    const raw = localStorage.getItem(getTemplateDraftStorageKey(templateId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearTemplateDraft = (templateId) => {
  if (!templateId) return;
  localStorage.removeItem(getTemplateDraftStorageKey(templateId));
};

const persistTemplateDraft = (templateId, layout) => {
  if (!templateId) return;
  localStorage.setItem(getTemplateDraftStorageKey(templateId), JSON.stringify({
    saved_at: new Date().toISOString(),
    layout,
  }));
};

const normalizeLayoutSignature = (layout) => JSON.stringify({
  canvasWidth: Number(layout?.canvasWidth) || A4_WIDTH,
  canvasHeight: Number(layout?.canvasHeight) || A4_HEIGHT,
  isThermal: Boolean(layout?.isThermal),
  autoHeight: Boolean(layout?.autoHeight),
  grid: layout?.grid || layout?.gridSettings || DEFAULT_GRID_SETTINGS,
  elements: normalizeLayoutElements(layout?.elements || []),
});

const shouldUseDraft = (draft, templateUpdatedAt) => {
  if (!draft?.layout?.elements) return false;
  if (!templateUpdatedAt) return true;

  const draftTime = Date.parse(draft.saved_at || '');
  const serverTime = Date.parse(templateUpdatedAt || '');

  if (Number.isNaN(draftTime) || Number.isNaN(serverTime)) {
    return true;
  }

  return draftTime > serverTime;
};

const isStructuredPosLayout = (layout) => Boolean(
  layout?.isThermal
  && Array.isArray(layout?.elements)
  && layout.elements.some((element) => [
    'pos-items-title',
    'pos-items-table',
    'pos-tax-title',
    'pos-tax-table',
  ].includes(element?.role))
);

const normalizeLayoutElements = (incomingElements = []) => {
  const seenIds = new Set();

  return resequenceZIndexes(
    incomingElements
      .filter((element) => element && typeof element === 'object')
      .map((element, index) => {
        let nextId = typeof element.id === 'string' && element.id.trim() ? element.id.trim() : `${generateId()}-${index}`;
        while (seenIds.has(nextId)) {
          nextId = `${generateId()}-${index}`;
        }
        seenIds.add(nextId);

        return {
          ...element,
          id: nextId,
          x: Number.isFinite(Number(element.x)) ? Number(element.x) : 0,
          y: Number.isFinite(Number(element.y)) ? Number(element.y) : 0,
          width: Math.max(20, Number.isFinite(Number(element.width)) ? Number(element.width) : (DEFAULT_ELEMENT_PROPS[element.type]?.width || 120)),
          height: Math.max(10, Number.isFinite(Number(element.height)) ? Number(element.height) : (DEFAULT_ELEMENT_PROPS[element.type]?.height || 24)),
        };
      })
  );
};

// Catálogo do frontend passa a consumir o contrato canónico do backend,
// mantendo fallback local apenas como camada de segurança durante a transição.

const getElementTypeLabel = (type) => {
  const labels = {
    text: 'Texto',
    variable: 'Variável',
    image: 'Imagem',
    rect: 'Rectângulo',
    line: 'Linha',
    table: 'Tabela',
  };
  return labels[type] || type || 'Elemento';
};

const buildLayoutDiffSummary = (currentLayout, compareLayout) => {
  const currentElements = currentLayout?.elements || [];
  const compareElements = compareLayout?.elements || [];
  const compareMap = new Map(compareElements.map((element) => [element.id, element]));
  const currentMap = new Map(currentElements.map((element) => [element.id, element]));

  const changed = currentElements.filter((element) => {
    const other = compareMap.get(element.id);
    return other && JSON.stringify(element) !== JSON.stringify(other);
  });
  const added = compareElements.filter((element) => !currentMap.has(element.id));
  const removed = currentElements.filter((element) => !compareMap.has(element.id));

  return {
    zoomChanged: currentLayout?.zoom !== compareLayout?.zoom,
    gridChanged: JSON.stringify(currentLayout?.grid || {}) !== JSON.stringify(compareLayout?.grid || {}),
    changedElements: changed,
    addedElements: added,
    removedElements: removed,
  };
};

const isElementErpBound = (element) => element?.type === 'variable' || element?.type === 'table';

const getElementErpBindingKey = (element) => {
  if (element?.type === 'variable') return element.variablePath || '';
  if (element?.type === 'table') return element.datasource || '';
  return '';
};

const buildCatalogInjectedElement = (item, selectedElement, tableDatasources = FALLBACK_TABLE_DATASOURCES) => {
  const isDatasource = item?.kind === 'datasource';
  const tableMeta = getTableDatasourceMeta(item?.path, tableDatasources);
  const fallbackType = isDatasource ? 'table' : 'variable';
  const base = {
    ...(DEFAULT_ELEMENT_PROPS[fallbackType] || {}),
    id: selectedElement?.id || generateId(),
    x: Number.isFinite(Number(selectedElement?.x)) ? Number(selectedElement.x) : 60,
    y: Number.isFinite(Number(selectedElement?.y)) ? Number(selectedElement.y) : 60,
    width: Math.max(20, Number.isFinite(Number(selectedElement?.width)) ? Number(selectedElement.width) : (DEFAULT_ELEMENT_PROPS[fallbackType]?.width || 160)),
    height: Math.max(10, Number.isFinite(Number(selectedElement?.height)) ? Number(selectedElement.height) : (DEFAULT_ELEMENT_PROPS[fallbackType]?.height || 24)),
    zIndex: Number.isFinite(Number(selectedElement?.zIndex)) ? Number(selectedElement.zIndex) : 0,
  };

  if (isDatasource) {
    return {
      ...base,
      type: 'table',
      datasource: item.path,
      columns: cloneDatasourceColumns(tableMeta?.defaultColumns || DEFAULT_ELEMENT_PROPS.table.columns || []),
      backgroundColor: selectedElement?.backgroundColor || DEFAULT_ELEMENT_PROPS.table.backgroundColor,
      borderColor: selectedElement?.borderColor || DEFAULT_ELEMENT_PROPS.table.borderColor,
      borderWidth: Number.isFinite(Number(selectedElement?.borderWidth)) ? Number(selectedElement.borderWidth) : DEFAULT_ELEMENT_PROPS.table.borderWidth,
      color: selectedElement?.color || DEFAULT_ELEMENT_PROPS.table.color,
      fontSize: Number.isFinite(Number(selectedElement?.fontSize)) ? Number(selectedElement.fontSize) : DEFAULT_ELEMENT_PROPS.table.fontSize,
    };
  }

  return {
    ...base,
    type: 'variable',
    variablePath: item.path,
    text: item.label || '',
    color: selectedElement?.color || DEFAULT_ELEMENT_PROPS.variable.color,
    backgroundColor: selectedElement?.backgroundColor || DEFAULT_ELEMENT_PROPS.variable.backgroundColor,
    borderColor: selectedElement?.borderColor || DEFAULT_ELEMENT_PROPS.variable.borderColor,
    borderWidth: Number.isFinite(Number(selectedElement?.borderWidth)) ? Number(selectedElement.borderWidth) : DEFAULT_ELEMENT_PROPS.variable.borderWidth,
    fontSize: Number.isFinite(Number(selectedElement?.fontSize)) ? Number(selectedElement.fontSize) : DEFAULT_ELEMENT_PROPS.variable.fontSize,
    fontWeight: selectedElement?.fontWeight || DEFAULT_ELEMENT_PROPS.variable.fontWeight,
    fontStyle: selectedElement?.fontStyle || DEFAULT_ELEMENT_PROPS.variable.fontStyle,
    textAlign: selectedElement?.textAlign || DEFAULT_ELEMENT_PROPS.variable.textAlign,
  };
};

const StaticLayoutPreview = ({ layout, tone = 'neutral', framed = true, scale = 0.34 }) => {
  const elements = layout?.elements || [];
  const palette = {
    neutral: { border: '#cbd5e1', text: '#0f172a', table: '#e2e8f0', box: '#ffffff', image: '#cbd5e1' },
    current: { border: '#2563eb', text: '#1d4ed8', table: '#dbeafe', box: 'rgba(255,255,255,0.92)', image: '#93c5fd' },
    compare: { border: '#d97706', text: '#b45309', table: '#ffedd5', box: 'rgba(255,255,255,0.72)', image: '#fdba74' },
  }[tone] || { border: '#cbd5e1', text: '#0f172a', table: '#e2e8f0', box: '#ffffff', image: '#cbd5e1' };

  const content = (
    <>
      <div
        className="relative origin-top-left bg-white shadow-sm"
        style={{ width: `${(layout?.canvasWidth || A4_WIDTH) * scale}px`, height: `${(layout?.canvasHeight || A4_HEIGHT) * scale}px` }}
      >
        {elements.map((element) => {
          const commonStyle = {
            position: 'absolute',
            left: `${(element.x || 0) * scale}px`,
            top: `${(element.y || 0) * scale}px`,
            width: `${Math.max(6, (element.width || 40) * scale)}px`,
            height: `${Math.max(4, (element.height || 12) * scale)}px`,
            overflow: 'hidden',
            borderColor: palette.border,
          };

          if (element.type === 'image') {
            return (
              <div key={element.id} style={{ ...commonStyle, border: `1px solid ${palette.image}`, background: '#fff' }}>
                {(element.dataUrl || element.imageUrl) ? (
                  <img src={element.dataUrl || element.imageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : null}
              </div>
            );
          }

          if (element.type === 'line') {
            return (
              <div
                key={element.id}
                style={{
                  ...commonStyle,
                  height: `${Math.max(1, element.strokeWidth || 1)}px`,
                  background: palette.border,
                }}
              />
            );
          }

          if (element.type === 'rect') {
            return <div key={element.id} style={{ ...commonStyle, border: `1px solid ${palette.border}`, background: 'transparent' }} />;
          }

          if (element.type === 'table') {
            return (
              <div key={element.id} style={{ ...commonStyle, border: `1px solid ${palette.border}`, background: palette.table, fontSize: '8px', color: palette.text, padding: '2px' }}>
                {`Tabela: ${element.datasource || 'items'}${element.datasource === 'company_banks' && (element.presentationMode || 'table') === 'list' ? ' • lista' : ''}`}
              </div>
            );
          }

          const textValue = element.type === 'variable'
            ? `{${element.variablePath || 'variável'}}`
            : getElementDisplayLabel(element, FALLBACK_VARIABLE_GROUPS, FALLBACK_TABLE_DATASOURCES);

          return (
            <div
              key={element.id}
              style={{
                ...commonStyle,
                color: palette.text,
                fontSize: `${Math.max(7, (element.fontSize || 10) * scale)}px`,
                fontWeight: element.fontStyle?.includes('bold') ? 700 : 500,
                fontStyle: element.fontStyle?.includes('italic') ? 'italic' : 'normal',
                background: palette.box,
                whiteSpace: 'nowrap',
              }}
            >
              {textValue}
            </div>
          );
        })}
      </div>
    </>
  );

  if (!framed) {
    return <div data-testid={`layout-diff-preview-${tone}`}>{content}</div>;
  }

  return (
    <div className="overflow-auto rounded-xl border bg-slate-100 p-3" data-testid={`layout-diff-preview-${tone}`}>
      {content}
    </div>
  );
};

// Generate unique ID
const generateId = () => `elem-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Default element properties
const DEFAULT_ELEMENT_PROPS = {
  text: {
    type: 'text',
    width: 150,
    height: 24,
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'left',
    color: '#000000',
    backgroundColor: 'transparent',
    borderColor: '#cccccc',
    borderWidth: 0,
    text: 'Texto estático',
  },
  variable: {
    type: 'variable',
    width: 150,
    height: 24,
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'left',
    color: '#1e40af',
    backgroundColor: '#f0f9ff',
    borderColor: '#3b82f6',
    borderWidth: 1,
    text: '',
    variablePath: 'customer.name',
  },
  image: {
    type: 'image',
    width: 100,
    height: 60,
    imageUrl: '',
    dataUrl: '', // P0-TB1.1: Base64 image data
    lockAspectRatio: true, // Lock aspect ratio by default
    originalWidth: 0,
    originalHeight: 0,
    backgroundColor: '#f5f5f5',
    borderColor: '#cccccc',
    borderWidth: 1,
  },
  rect: {
    type: 'rect',
    width: 200,
    height: 100,
    backgroundColor: 'transparent',
    borderColor: '#000000',
    borderWidth: 1,
  },
  line: {
    type: 'line',
    width: 200,
    height: 2,
    backgroundColor: 'transparent',
    borderColor: '#000000',
    borderWidth: 1,
  },
  table: {
    type: 'table',
    width: 400,
    height: 150,
    fontSize: 10,
    color: '#000000',
    backgroundColor: '#ffffff',
    borderColor: '#000000',
    borderWidth: 1,
    datasource: 'items',
    columns: cloneDatasourceColumns(FALLBACK_TABLE_DATASOURCES[0].defaultColumns),
  },
};

// Hook to load image from dataUrl / imageUrl
const useImage = (imageSource) => {
  const [image, setImage] = useState(null);
  
  useEffect(() => {
    if (!imageSource) {
      setImage(null);
      return;
    }
    
    const img = new window.Image();
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
    img.src = imageSource;
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageSource]);
  
  return image;
};

// Canvas Element Component
const CanvasElement = ({ element, isSelected, onSelect, onChange, onDelete, gridSettings, gridSpacingPt, onRegisterRef, onInteractionLock, variableOptions = FALLBACK_VARIABLE_GROUPS, tableDatasources = FALLBACK_TABLE_DATASOURCES, validationState = null }) => {
  const shapeRef = useRef();
  const hasInvalidErpBinding = Boolean(validationState?.invalid);
  
  // Register ref with parent on mount/update
  useEffect(() => {
    if (onRegisterRef && shapeRef.current) {
      onRegisterRef(element.id, shapeRef.current);
    }
    return () => {
      if (onRegisterRef) {
        onRegisterRef(element.id, null);
      }
    };
  }, [element.id, onRegisterRef]);
  
  // Load image if element is image type with dataUrl or imageUrl
  const loadedImage = useImage(element.type === 'image' ? (element.dataUrl || element.imageUrl || null) : null);

  const blockStageClick = () => {
    if (onInteractionLock) onInteractionLock();
  };

  const handleDragMove = (e) => {
    blockStageClick();
    if (e?.evt) {
      e.evt.cancelBubble = true;
    }

    const shouldSnap = gridSettings?.enabled && gridSettings?.snap && !e.evt?.altKey;
    if (!shouldSnap || !gridSpacingPt) return;

    e.target.position({
      x: snapToGrid(e.target.x(), gridSpacingPt),
      y: snapToGrid(e.target.y(), gridSpacingPt),
    });
  };

  const handleDragEnd = (e) => {
    blockStageClick();
    if (e?.evt) {
      e.evt.cancelBubble = true;
    }
    let x = e.target.x();
    let y = e.target.y();
    
    // FIX: Snapping Logic
    const shouldSnap = gridSettings?.enabled && gridSettings?.snap && !e.evt?.altKey;
    if (shouldSnap && gridSpacingPt) {
      x = snapToGrid(x, gridSpacingPt);
      y = snapToGrid(y, gridSpacingPt);
    }

    onChange({
      ...element,
      x: roundTo2(x),
      y: roundTo2(y),
    });
    
    // Reset visual position to snapped if needed (Konva might leave it at drop point)
    e.target.x(x);
    e.target.y(y);
  };

  const handleTransformEnd = (e) => {
    blockStageClick();
    if (e?.evt) {
      e.evt.cancelBubble = true;
    }
    const node = shapeRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    let x = node.x();
    let y = node.y();
    let width = Math.max(20, node.width() * scaleX);
    let height = Math.max(10, node.height() * scaleY);

    const shouldSnap = gridSettings?.enabled && gridSettings?.snap && !e.evt?.altKey;
    if (shouldSnap && gridSpacingPt) {
      x = snapToGrid(x, gridSpacingPt);
      y = snapToGrid(y, gridSpacingPt);
      width = Math.max(20, snapToGrid(width, gridSpacingPt));
      height = Math.max(10, snapToGrid(height, gridSpacingPt));
    }

    node.x(x);
    node.y(y);

    onChange({
      ...element,
      x: roundTo2(x),
      y: roundTo2(y),
      width: roundTo2(width),
      height: roundTo2(height),
    });
  };

  const getDisplayText = () => {
    if (element.type === 'variable') {
      const varOption = flattenVariableOptions(variableOptions).find((item) => item.path === element.variablePath);
      return `{${varOption?.label || element.variablePath}}`;
    }
    return element.text || '';
  };

  const commonProps = {
    ref: shapeRef,
    x: element.x,
    y: element.y,
    draggable: true,
    onMouseDown: (e) => onSelect(element.id, e),
    onTap: (e) => onSelect(element.id, e),
    onDragStart: (e) => {
      blockStageClick();
      if (e?.evt) {
        e.evt.cancelBubble = true;
      }
      onSelect(element.id, e);
    },
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onTransformStart: (e) => {
      blockStageClick();
      if (e?.evt) {
        e.evt.cancelBubble = true;
      }
    },
    onTransformEnd: handleTransformEnd,
  };

  const renderInvalidBindingOverlay = () => {
    if (!hasInvalidErpBinding) return null;
    return (
      <>
        <Rect
          width={element.width}
          height={element.height}
          stroke="#dc2626"
          strokeWidth={2}
          dash={[8, 4]}
          fill="transparent"
          listening={false}
        />
        <Group x={Math.max(0, element.width - 22)} y={0} listening={false}>
          <Rect width={22} height={18} fill="#fee2e2" cornerRadius={4} />
          <Text text="!" width={22} height={18} align="center" verticalAlign="middle" fontStyle="bold" fontSize={11} fill="#b91c1c" />
        </Group>
      </>
    );
  };

  const renderElement = () => {
    switch (element.type) {
      case 'text':
      case 'variable':
        return (
          <Group {...commonProps} width={element.width} height={element.height}>
            {element.backgroundColor !== 'transparent' && (
              <Rect
                width={element.width}
                height={element.height}
                fill={element.backgroundColor}
                stroke={element.borderWidth > 0 ? element.borderColor : null}
                strokeWidth={element.borderWidth}
              />
            )}
            <Text
              text={getDisplayText()}
              width={element.width}
              height={element.height}
              fontSize={element.fontSize}
              fontStyle={`${element.fontWeight === 'bold' ? 'bold' : ''} ${element.fontStyle === 'italic' ? 'italic' : ''}`.trim() || 'normal'}
              fill={element.color}
              align={element.textAlign}
              verticalAlign="middle"
              padding={4}
            />
            {renderInvalidBindingOverlay()}
          </Group>
        );

      case 'image':
        if (loadedImage && (element.dataUrl || element.imageUrl)) {
          return (
            <Group {...commonProps} width={element.width} height={element.height}>
              <KonvaImage
                image={loadedImage}
                width={element.width}
                height={element.height}
              />
              {element.borderWidth > 0 && (
                <Rect
                  width={element.width}
                  height={element.height}
                  stroke={element.borderColor}
                  strokeWidth={element.borderWidth}
                  fill="transparent"
                />
              )}
            </Group>
          );
        }
        return (
          <Group {...commonProps} width={element.width} height={element.height}>
            <Rect
              key={`${element.id}-table-body`}
              width={element.width}
              height={element.height}
              fill={element.backgroundColor}
              stroke={element.borderColor}
              strokeWidth={element.borderWidth}
            />
            <Text
              text="+ Carregar Imagem"
              width={element.width}
              height={element.height}
              fontSize={12}
              fill="#666"
              align="center"
              verticalAlign="middle"
            />
          </Group>
        );

      case 'rect':
        return (
          <Rect
            {...commonProps}
            width={element.width}
            height={element.height}
            fill={element.backgroundColor === 'transparent' ? null : element.backgroundColor}
            stroke={element.borderColor}
            strokeWidth={element.borderWidth}
          />
        );

      case 'line':
        return (
          <Line
            {...commonProps}
            points={[0, element.height / 2, element.width, element.height / 2]}
            stroke={element.borderColor}
            strokeWidth={element.borderWidth || 1}
          />
        );

      case 'table':
        const rowHeight = 20;
        const headerHeight = 24;
        const numRows = Math.floor((element.height - headerHeight) / rowHeight);
        const isBankDatasource = element.datasource === 'company_banks';
        const isBankList = isBankDatasource && (element.presentationMode || 'table') === 'list';
        const bankRows = COMPANY_BANK_PREVIEW_ROWS.slice(0, Math.max(1, Math.min(2, Math.floor((element.height - 20) / 58) || 1)));
        
        return (
          <Group {...commonProps} width={element.width} height={element.height}>
            <Rect
              width={element.width}
              height={element.height}
              fill={element.backgroundColor}
              stroke={element.borderColor}
              strokeWidth={element.borderWidth}
            />
            {isBankList ? (
              <>
                {bankRows.map((row, rowIndex) => {
                  const blockY = 8 + (rowIndex * 58);
                  const title = row.label || row.bank_name || `Banco ${rowIndex + 1}`;
                  const valueLines = (element.columns || []).slice(0, 4).map((column) => {
                    const field = column.field || 'label';
                    const value = row[field] || '—';
                    return `${column.label || field}: ${value}`;
                  });
                  const content = (element.bankDisposition || 'stacked') === 'inline'
                    ? valueLines.join(' • ')
                    : valueLines.join('\n');

                  return (
                    <Group key={`${element.id}-bank-row-${rowIndex}`}>
                      <Rect
                        x={8}
                        y={blockY}
                        width={element.width - 16}
                        height={50}
                        fill="#ffffff"
                        stroke="#cbd5e1"
                        strokeWidth={0.8}
                        cornerRadius={6}
                      />
                      <Text
                        x={16}
                        y={blockY + 6}
                        width={element.width - 32}
                        height={14}
                        text={title}
                        fontSize={Math.max(9, element.fontSize || 10)}
                        fontStyle="bold"
                        fill={element.color || '#0f172a'}
                      />
                      <Text
                        x={16}
                        y={blockY + 22}
                        width={element.width - 32}
                        height={24}
                        text={content}
                        fontSize={Math.max(7, (element.fontSize || 10) - 1)}
                        fill="#475569"
                      />
                    </Group>
                  );
                })}
              </>
            ) : (
              <>
                <Rect
                  key={`${element.id}-table-header-bg`}
                  y={0}
                  width={element.width}
                  height={headerHeight}
                  fill="#1e40af"
                />
                <Text
                  key={`${element.id}-table-header-text`}
                  text={element.columns?.map(c => c.label).join(' | ') || 'Tabela'}
                  width={element.width}
                  height={headerHeight}
                  fontSize={Math.max(8, element.fontSize || 10)}
                  fill="#ffffff"
                  fontStyle="bold"
                  align="center"
                  verticalAlign="middle"
                  padding={4}
                />
                {Array.from({ length: Math.min(numRows, 3) }).map((_, i) => (
                  <Group key={i}>
                    <Line
                      points={[0, headerHeight + (i + 1) * rowHeight, element.width, headerHeight + (i + 1) * rowHeight]}
                      stroke="#ddd"
                      strokeWidth={0.5}
                    />
                    <Text
                      y={headerHeight + i * rowHeight}
                      text={isBankDatasource ? (COMPANY_BANK_PREVIEW_ROWS[i]?.label || `Banco ${i + 1}`) : `Linha ${i + 1}`}
                      width={element.width}
                      height={rowHeight}
                      fontSize={Math.max(8, (element.fontSize || 10) - 1)}
                      fill="#666"
                      align="center"
                      verticalAlign="middle"
                    />
                  </Group>
                ))}
              </>
            )}
            <Text
              y={element.height - 16}
              text={`[${tableDatasources.find((item) => item.value === element.datasource)?.label || element.datasource}${isBankList ? ' • lista' : ''}]`}
              width={element.width}
              fontSize={8}
              fill="#999"
              align="center"
            />
            {renderInvalidBindingOverlay()}
          </Group>
        );

      default:
        return null;
    }
  };

  return renderElement();
};

// Properties Panel Component
const PropertiesPanel = ({ element, onUpdate, onDelete, onBringForward, onSendBackward, onBringToFront, onSendToBack, panelWidth = 304, variableOptions = FALLBACK_VARIABLE_GROUPS, tableDatasources = FALLBACK_TABLE_DATASOURCES, validationState = null }) => {
  const isNarrowPanel = panelWidth < 300;
  const isWidePanel = panelWidth > 380;
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState(null);

  useEffect(() => {
    setDraggedColumnIndex(null);
    setDragOverColumnIndex(null);
  }, [element?.id]);

  if (!element) {
    return (
      <div className={`flex h-full items-center justify-center px-4 text-center text-muted-foreground ${isNarrowPanel ? 'text-xs' : 'text-sm'}`}>
        <div>
          <p className="font-medium">Seleccione um elemento</p>
          <p className="mt-1">As propriedades aparecem aqui e ajustam-se à largura do painel.</p>
        </div>
      </div>
    );
  }

  const updateProp = (key, value) => {
    onUpdate({ ...element, [key]: value });
  };

  const tableDatasource = element.type === 'table' ? getTableDatasourceMeta(element.datasource, tableDatasources) : null;
  const tableFields = tableDatasource?.fields || [];
  const updateTableColumns = (nextColumns) => updateProp('columns', nextColumns);
  const moveTableColumn = (fromIndex, toIndex) => {
    const columns = [...(element.columns || [])];
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= columns.length || toIndex >= columns.length || fromIndex === toIndex) {
      return;
    }
    const [movedColumn] = columns.splice(fromIndex, 1);
    columns.splice(toIndex, 0, movedColumn);
    updateTableColumns(columns);
  };
  const updateTableColumn = (index, patch) => {
    const nextColumns = (element.columns || []).map((column, columnIndex) => (
      columnIndex === index ? { ...column, ...patch } : column
    ));
    updateTableColumns(nextColumns);
  };
  const addTableColumn = () => {
    const fallbackField = tableFields[0]?.value || 'description';
    const fallbackLabel = tableFields[0]?.label || 'Nova coluna';
    updateTableColumns([
      ...(element.columns || []),
      { field: fallbackField, label: fallbackLabel, width: 80, align: 'left' },
    ]);
  };
  const removeTableColumn = (index) => {
    updateTableColumns((element.columns || []).filter((_, columnIndex) => columnIndex !== index));
  };
  const resetDatasourceColumns = () => {
    if (!tableDatasource) return;
    updateTableColumns(cloneDatasourceColumns(tableDatasource.defaultColumns));
  };

  return (
    <ScrollArea className="h-full">
      <div className="space-y-3 p-3">
        {validationState?.invalid && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-2 text-[11px] text-red-700" data-testid="template-element-invalid-binding-alert">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                <p className="font-semibold">Ligação ERP inválida</p>
                <p>{validationState.message}</p>
              </div>
            </div>
          </div>
        )}
        {/* Position & Size */}
        <div>
          <Label className="text-xs font-semibold text-muted-foreground">POSIÇÃO E TAMANHO</Label>
          <div className={`mt-2 grid gap-1.5 ${isNarrowPanel ? 'grid-cols-2' : 'grid-cols-4'}`}>
            <div>
              <Label className="text-xs">X</Label>
              <Input
                type="number"
                value={Math.round(element.x)}
                onChange={(e) => updateProp('x', parseInt(e.target.value) || 0)}
                className="h-7 px-2 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Y</Label>
              <Input
                type="number"
                value={Math.round(element.y)}
                onChange={(e) => updateProp('y', parseInt(e.target.value) || 0)}
                className="h-7 px-2 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Largura</Label>
              <Input
                type="number"
                value={Math.round(element.width)}
                onChange={(e) => updateProp('width', parseInt(e.target.value) || 20)}
                className="h-7 px-2 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Altura</Label>
              <Input
                type="number"
                value={Math.round(element.height)}
                onChange={(e) => updateProp('height', parseInt(e.target.value) || 10)}
                className="h-7 px-2 text-xs"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Text/Variable specific */}
        {(element.type === 'text' || element.type === 'variable') && (
          <>
            {element.type === 'text' && (
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">TEXTO</Label>
                <Input
                  value={element.text || ''}
                  onChange={(e) => updateProp('text', e.target.value)}
                  placeholder="Digite o texto..."
                  className="mt-2"
                />
              </div>
            )}

            {element.type === 'variable' && (
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">VARIÁVEL</Label>
                <Select
                  value={element.variablePath || 'customer.name'}
                  onValueChange={(v) => updateProp('variablePath', v)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Seleccionar variável" />
                  </SelectTrigger>
                  <SelectContent>
                    {variableOptions.map((group) => (
                      <SelectGroup key={group.group}>
                        <SelectLabel key={`${group.group}-label`}>{group.group}</SelectLabel>
                        {group.items.map((item) => (
                          <SelectItem key={item.path} value={item.path}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-muted-foreground">FONTE</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs">Tamanho</Label>
                  <Input
                    type="number"
                    value={element.fontSize}
                    onChange={(e) => updateProp('fontSize', parseInt(e.target.value) || 12)}
                    className="h-8"
                    min={6}
                    max={72}
                  />
                </div>
                <div>
                  <Label className="text-xs">Cor</Label>
                  <Input
                    type="color"
                    value={element.color}
                    onChange={(e) => updateProp('color', e.target.value)}
                    className="h-8 p-1"
                  />
                </div>
              </div>
              <div className="flex gap-1 mt-2">
                <Button
                  variant={element.fontWeight === 'bold' ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateProp('fontWeight', element.fontWeight === 'bold' ? 'normal' : 'bold')}
                >
                  <Bold className="h-4 w-4" />
                </Button>
                <Button
                  variant={element.fontStyle === 'italic' ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateProp('fontStyle', element.fontStyle === 'italic' ? 'normal' : 'italic')}
                >
                  <Italic className="h-4 w-4" />
                </Button>
                <Separator orientation="vertical" className="h-8" />
                <Button
                  variant={element.textAlign === 'left' ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateProp('textAlign', 'left')}
                >
                  <AlignLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant={element.textAlign === 'center' ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateProp('textAlign', 'center')}
                >
                  <AlignCenter className="h-4 w-4" />
                </Button>
                <Button
                  variant={element.textAlign === 'right' ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateProp('textAlign', 'right')}
                >
                  <AlignRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Image specific - P0-TB1.1: Upload de imagem */}
        {element.type === 'image' && (
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-muted-foreground">IMAGEM</Label>
            
            {/* File upload button */}
            <div>
              <input
                type="file"
                id="image-upload"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  
                  // Validate file type
                  if (!file.type.match(/^image\/(png|jpeg|jpg|svg\+xml)$/)) {
                    toast.error('Formato não suportado. Use PNG, JPG ou SVG.');
                    return;
                  }
                  
                  try {
                    toast.loading('A processar imagem...');
                    const { dataUrl, width, height } = await compressImage(file);
                    
                    // Calculate aspect ratio and set initial size
                    const aspectRatio = width / height;
                    const newWidth = Math.min(200, width);
                    const newHeight = Math.round(newWidth / aspectRatio);
                    
                    onUpdate({
                      ...element,
                      dataUrl,
                      originalWidth: width,
                      originalHeight: height,
                      width: newWidth,
                      height: newHeight,
                      lockAspectRatio: true,
                    });
                    
                    toast.dismiss();
                    toast.success('Imagem carregada com sucesso');
                  } catch (error) {
                    toast.dismiss();
                    toast.error('Erro ao processar imagem: ' + error.message);
                  }
                  
                  // Reset input
                  e.target.value = '';
                }}
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => document.getElementById('image-upload').click()}
                data-testid="upload-image-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {(element.dataUrl || element.imageUrl) ? 'Substituir Imagem' : 'Carregar Imagem'}
              </Button>
            </div>
            <div>
              <Label className="text-xs">URL / caminho existente</Label>
              <Input
                value={element.imageUrl || ''}
                onChange={(e) => onUpdate({ ...element, imageUrl: e.target.value })}
                className="mt-1 h-8 text-xs"
                placeholder="/uploads/logos/logo_cg_primary.png"
                data-testid="image-url-input"
              />
            </div>
            
            {/* Image preview */}
            {(element.dataUrl || element.imageUrl) && (
              <div className="space-y-2">
                <div className="relative border rounded overflow-hidden bg-gray-100">
                  <img 
                    src={element.dataUrl || element.imageUrl} 
                    alt="Preview" 
                    className="w-full h-auto max-h-32 object-contain"
                    data-testid="image-preview-thumbnail"
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span data-testid="image-preview-dimensions">{element.originalWidth ? `${element.originalWidth}x${element.originalHeight}px` : 'Fonte externa'}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-red-500 hover:text-red-700"
                    onClick={() => onUpdate({ ...element, dataUrl: '', imageUrl: '', originalWidth: 0, originalHeight: 0 })}
                    data-testid="remove-image-source-button"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Remover
                  </Button>
                </div>
              </div>
            )}
            
            {/* Lock aspect ratio toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-xs">Manter proporção</Label>
              <Button
                variant={element.lockAspectRatio ? 'default' : 'outline'}
                size="icon"
                className="h-7 w-7"
                onClick={() => onUpdate({ ...element, lockAspectRatio: !element.lockAspectRatio })}
                title={element.lockAspectRatio ? 'Proporção bloqueada' : 'Proporção livre'}
              >
                {element.lockAspectRatio ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        )}

        {/* Table specific */}
        {element.type === 'table' && (
          <div className="space-y-2.5" data-testid="freeform-table-editor-panel">
            <Label className="text-xs font-semibold text-muted-foreground">TABELA</Label>
            <Select
              value={element.datasource || tableDatasources[0]?.value || FALLBACK_TABLE_DATASOURCES[0].value}
              onValueChange={(v) => {
                const ds = getTableDatasourceMeta(v, tableDatasources);
                onUpdate({
                  ...element,
                  datasource: v,
                  columns: cloneDatasourceColumns(ds.defaultColumns),
                  presentationMode: v === 'company_banks' ? (element.presentationMode || 'table') : undefined,
                  bankDisposition: v === 'company_banks' ? (element.bankDisposition || 'stacked') : undefined,
                });
              }}
            >
              <SelectTrigger className="mt-2" data-testid="freeform-table-datasource-select">
                <SelectValue placeholder="Fonte de dados" />
              </SelectTrigger>
              <SelectContent>
                {tableDatasources.map((ds) => (
                  <SelectItem key={ds.value} value={ds.value}>
                    {ds.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className={`grid gap-2 ${isNarrowPanel ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid="freeform-table-visual-controls">
              <div>
                <Label className="text-[11px]">Tamanho da fonte</Label>
                <Input
                  type="number"
                  value={element.fontSize || 10}
                  onChange={(e) => updateProp('fontSize', parseInt(e.target.value, 10) || 10)}
                  className="mt-1 h-8 text-xs"
                  min={7}
                  max={24}
                  step={1}
                  data-testid="freeform-table-font-size-input"
                />
              </div>
              {element.datasource === 'company_banks' && (
                <div>
                  <Label className="text-[11px]">Apresentação bancária</Label>
                  <Select value={element.presentationMode || 'table'} onValueChange={(value) => onUpdate({ ...element, presentationMode: value })}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="freeform-bank-presentation-mode-select">
                      <SelectValue placeholder="Apresentação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="table">Tabela</SelectItem>
                      <SelectItem value="list">Lista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {element.datasource === 'company_banks' && (
              <div>
                <Label className="text-[11px]">Disposição dos dados</Label>
                <Select value={element.bankDisposition || 'stacked'} onValueChange={(value) => onUpdate({ ...element, bankDisposition: value })}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="freeform-bank-disposition-select">
                    <SelectValue placeholder="Disposição" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stacked">Detalhada</SelectItem>
                    <SelectItem value="inline">Compacta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5" data-testid="freeform-table-columns-actions">
              <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={addTableColumn} data-testid="freeform-table-add-column-btn">
                Nova coluna
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={resetDatasourceColumns} data-testid="freeform-table-reset-columns-btn">
                Repor colunas do datasource
              </Button>
            </div>
            <div className={`space-y-1.5 rounded-lg border bg-muted/30 p-2 ${isNarrowPanel ? 'text-[11px]' : 'text-xs'}`} data-testid="freeform-table-columns-list">
              <div key={`${element.id}-columns-title`} className="font-medium">Colunas editáveis</div>
              <div className="text-[11px] text-muted-foreground" data-testid="freeform-table-columns-help-text">
                Pode editar, mover por arrasto e usar os botões para reposicionar a ordem das colunas.
              </div>
              {(element.columns || []).map((col, i) => (
                <div
                  key={`${element.id}-column-${i}`}
                  className={`rounded-md border bg-background p-2 transition-colors ${dragOverColumnIndex === i ? 'border-primary bg-primary/5' : ''}`}
                  data-testid={`freeform-table-column-${i}`}
                  draggable
                  onDragStart={() => {
                    setDraggedColumnIndex(i);
                    setDragOverColumnIndex(i);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (dragOverColumnIndex !== i) {
                      setDragOverColumnIndex(i);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (draggedColumnIndex !== null) {
                      moveTableColumn(draggedColumnIndex, i);
                    }
                    setDraggedColumnIndex(null);
                    setDragOverColumnIndex(null);
                  }}
                  onDragEnd={() => {
                    setDraggedColumnIndex(null);
                    setDragOverColumnIndex(null);
                  }}
                >
                  <div className="mb-2 flex items-center justify-between gap-2" data-testid={`freeform-table-column-toolbar-${i}`}>
                    <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
                      <GripVertical className="h-3.5 w-3.5 cursor-grab" />
                      <span data-testid={`freeform-table-column-position-${i}`}>Posição {i + 1}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => moveTableColumn(i, i - 1)}
                        disabled={i === 0}
                        data-testid={`freeform-table-column-move-left-${i}`}
                        title="Mover para a esquerda"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => moveTableColumn(i, i + 1)}
                        disabled={i === (element.columns || []).length - 1}
                        data-testid={`freeform-table-column-move-right-${i}`}
                        title="Mover para a direita"
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className={`grid gap-1.5 items-end ${isNarrowPanel ? 'grid-cols-1' : 'grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_88px_108px_auto]'}`}>
                    <div>
                      <Label className="text-[11px]">Título</Label>
                      <Input
                        value={col.label || ''}
                        onChange={(e) => updateTableColumn(i, { label: e.target.value })}
                        className="mt-1 h-7 px-2 text-xs"
                        data-testid={`freeform-table-column-label-${i}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">Campo</Label>
                      {tableFields.length > 0 ? (
                        <Select
                          key={`${element.id}-${i}-field-select`}
                          value={col.field || tableFields[0]?.value || 'description'}
                          onValueChange={(value) => {
                            const fieldMeta = tableFields.find((field) => field.value === value);
                            updateTableColumn(i, { field: value, label: col.label || fieldMeta?.label || value });
                          }}
                        >
                          <SelectTrigger className="mt-1 h-7 text-xs" data-testid={`freeform-table-column-field-${i}`}>
                            <SelectValue placeholder="Campo" />
                          </SelectTrigger>
                          <SelectContent>
                            {tableFields.map((field) => (
                              <SelectItem key={`${element.id}-${i}-${field.value}`} value={field.value}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={col.field || ''}
                          onChange={(e) => updateTableColumn(i, { field: e.target.value })}
                          className="mt-1 h-7 px-2 text-xs"
                          data-testid={`freeform-table-column-field-${i}`}
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-[11px]">Largura (pt)</Label>
                      <Input
                        type="number"
                        value={col.width || 80}
                        onChange={(e) => updateTableColumn(i, { width: parseFloat(e.target.value) || 20 })}
                        className="mt-1 h-7 px-2 text-xs"
                        min={20}
                        step={1}
                        data-testid={`freeform-table-column-width-${i}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">Alinhamento</Label>
                      <Select
                        key={`${element.id}-${i}-align-select`}
                        value={col.align || 'left'}
                        onValueChange={(value) => updateTableColumn(i, { align: value })}
                      >
                        <SelectTrigger className="mt-1 h-7 text-xs" data-testid={`freeform-table-column-align-${i}`}>
                          <SelectValue placeholder="Alinhamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">Esquerda</SelectItem>
                          <SelectItem value="center">Centro</SelectItem>
                          <SelectItem value="right">Direita</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-600"
                      onClick={() => removeTableColumn(i)}
                      data-testid={`freeform-table-column-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {(!element.columns || element.columns.length === 0) && (
                <div key={`${element.id}-columns-empty`} className="rounded-md border border-dashed p-3 text-[11px] text-muted-foreground" data-testid="freeform-table-columns-empty">
                  Esta tabela ainda não tem colunas. Use <strong>Nova coluna</strong> ou <strong>Repor colunas do datasource</strong>.
                </div>
              )}
            </div>
          </div>
        )}

        <Separator />

        {/* Style - Common */}
        {element.type !== 'line' && (
          <div>
            <Label className="text-xs font-semibold text-muted-foreground">FUNDO E BORDA</Label>
            <div className={`mt-2 grid gap-1.5 items-end ${isNarrowPanel ? 'grid-cols-2' : 'grid-cols-[74px_1fr_74px]'}`}>
              <div>
                <Label className="text-xs">Fundo</Label>
                <Input
                  type="color"
                  value={element.backgroundColor === 'transparent' ? '#ffffff' : element.backgroundColor}
                  onChange={(e) => updateProp('backgroundColor', e.target.value)}
                  className="h-7 p-1"
                />
              </div>
              <div>
                <Label className="text-xs">Espessura: {element.borderWidth}px</Label>
                <Slider
                  value={[element.borderWidth]}
                  onValueChange={([v]) => updateProp('borderWidth', v)}
                  min={0}
                  max={5}
                  step={0.5}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-xs">Borda</Label>
                <Input
                  type="color"
                  value={element.borderColor}
                  onChange={(e) => updateProp('borderColor', e.target.value)}
                  className="h-7 p-1"
                />
              </div>
            </div>
            <div className="mt-1.5 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-[11px]"
                onClick={() => updateProp('backgroundColor', 'transparent')}
              >
                Fundo nenhum
              </Button>
            </div>
          </div>
        )}

        {/* Line specific */}
        {element.type === 'line' && (
          <div>
            <Label className="text-xs font-semibold text-muted-foreground">LINHA</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="text-xs">Cor</Label>
                <Input
                  type="color"
                  value={element.borderColor}
                  onChange={(e) => updateProp('borderColor', e.target.value)}
                  className="h-8 p-1"
                />
              </div>
              <div>
                <Label className="text-xs">Espessura</Label>
                <Input
                  type="number"
                  value={element.borderWidth}
                  onChange={(e) => updateProp('borderWidth', parseFloat(e.target.value) || 1)}
                  className="h-8"
                  min={0.5}
                  max={10}
                  step={0.5}
                />
              </div>
            </div>
          </div>
        )}

        <Separator />

        {/* Z-Index controls */}
        <div>
          <Label className="text-xs font-semibold text-muted-foreground">CAMADA (Z-INDEX)</Label>
          <div className={`mt-2 grid gap-1.5 ${isNarrowPanel ? 'grid-cols-2' : 'grid-cols-4'}`}>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onBringForward?.(element.id)}
              data-testid="element-zindex-forward"
            >
              <ChevronUp className="mr-1 h-3.5 w-3.5" /> Frente
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onSendBackward?.(element.id)}
              data-testid="element-zindex-backward"
            >
              <ChevronDown className="mr-1 h-3.5 w-3.5" /> Trás
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onBringToFront?.(element.id)}
              data-testid="element-zindex-to-front"
            >
              Topo
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onSendToBack?.(element.id)}
              data-testid="element-zindex-to-back"
            >
              Base
            </Button>
          </div>
        </div>

        <Separator />

        {/* Delete */}
        <Button
          variant="destructive"
          className="w-full"
          onClick={() => onDelete(element.id)}
        >
          <Trash2 className="h-4 w-4 mr-2" /> Eliminar Elemento
        </Button>
      </div>
    </ScrollArea>
  );
};

// Main FreeformCanvas Component
export default function FreeformCanvas({ 
  templateId,
  initialLayout,
  templateUpdatedAt,
  availableTemplates = [],
  onSave,
  onPreview 
}) {
  const [elements, setElements] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // FIX: Changed to Array for Multi-Select
  const [elementSearch, setElementSearch] = useState('');
  const [sidePanelTab, setSidePanelTab] = useState('properties');
  const [jsonEditorValue, setJsonEditorValue] = useState('');
  const [jsonDirty, setJsonDirty] = useState(false);
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [diffViewMode, setDiffViewMode] = useState('side-by-side');
  const [compareTemplateId, setCompareTemplateId] = useState('');
  const [compareElementId, setCompareElementId] = useState('');
  const [bulkReferenceDialogOpen, setBulkReferenceDialogOpen] = useState(false);
  const [zoom, setZoom] = useState(0.8);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [gridSettings, setGridSettings] = useState(DEFAULT_GRID_SETTINGS);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('cg-freeform-pane-widths-v1') || '{}');
      return Math.min(360, Math.max(208, Number(saved.left) || 224));
    } catch {
      return 224;
    }
  });
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('cg-freeform-pane-widths-v1') || '{}');
      return Math.min(420, Math.max(272, Number(saved.right) || 304));
    } catch {
      return 304;
    }
  });
  const [resizingPanel, setResizingPanel] = useState(null);
  const [erpCatalog, setErpCatalog] = useState(null);
  const [catalogInsertDialogOpen, setCatalogInsertDialogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogModuleFilter, setCatalogModuleFilter] = useState('__all__');
  const [catalogDocTypeFilter, setCatalogDocTypeFilter] = useState('__all__');
  const [draftBaselineSignature, setDraftBaselineSignature] = useState('');
  
  const stageRef = useRef();
  const trRef = useRef();
  const shapeRefs = useRef({}); // Refs map: elementId -> konvaNode
  const stageScrollRef = useRef();
  const stageInteractionLockRef = useRef(0);
  const canvasRootRef = useRef();
  const restoredDraftToastRef = useRef(new Set());
  const variableOptions = useMemo(() => buildVariableOptionsFromCatalog(erpCatalog), [erpCatalog]);
  const flatVariableOptions = useMemo(() => flattenVariableOptions(variableOptions), [variableOptions]);
  const tableDatasourceOptions = useMemo(() => buildTableDatasourcesFromCatalog(erpCatalog), [erpCatalog]);
  const catalogItems = useMemo(() => erpCatalog?.flat_items || [], [erpCatalog]);
  const catalogModuleOptions = useMemo(() => erpCatalog?.modules || [], [erpCatalog]);
  const catalogDocTypeOptions = useMemo(() => erpCatalog?.doc_types || [], [erpCatalog]);
  const filteredCatalogItems = useMemo(() => {
    const query = catalogSearch.trim().toLowerCase();
    return catalogItems.filter((item) => {
      const matchesSearch = !query || [
        item.path,
        item.label,
        item.group,
        item.module,
        ...(item.doc_types || []),
      ].filter(Boolean).join(' ').toLowerCase().includes(query);
      const matchesModule = catalogModuleFilter === '__all__' || item.module === catalogModuleFilter;
      const matchesDocType = catalogDocTypeFilter === '__all__' || (item.doc_types || []).includes(catalogDocTypeFilter) || (item.doc_types || []).includes('*');
      return matchesSearch && matchesModule && matchesDocType;
    });
  }, [catalogDocTypeFilter, catalogItems, catalogModuleFilter, catalogSearch]);

  const validVariablePaths = useMemo(
    () => new Set(flatVariableOptions.map((item) => item.path).filter(Boolean)),
    [flatVariableOptions]
  );
  const validDatasourcePaths = useMemo(
    () => new Set(tableDatasourceOptions.map((item) => item.value).filter(Boolean)),
    [tableDatasourceOptions]
  );
  const elementValidationMap = useMemo(() => {
    const validationEntries = elements.map((element) => {
      if (!isElementErpBound(element)) {
        return [element.id, { invalid: false, message: '' }];
      }

      if (element.type === 'variable') {
        const isValid = Boolean(element.variablePath) && validVariablePaths.has(element.variablePath);
        return [element.id, {
          invalid: !isValid,
          message: isValid ? '' : `O path ERP "${element.variablePath || 'vazio'}" já não existe no catálogo ERP.`,
        }];
      }

      const isValid = Boolean(element.datasource) && validDatasourcePaths.has(element.datasource);
      return [element.id, {
        invalid: !isValid,
        message: isValid ? '' : `A datasource "${element.datasource || 'vazia'}" já não existe no catálogo ERP.`,
      }];
    });
    return Object.fromEntries(validationEntries);
  }, [elements, validDatasourcePaths, validVariablePaths]);
  const invalidBindingCount = useMemo(
    () => Object.values(elementValidationMap).filter((item) => item?.invalid).length,
    [elementValidationMap]
  );

  const loadCatalog = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates/erp-elements-catalog`, {
        headers: buildAuthHeaders(),
      });
      if (!response.ok) return;
      const payload = await response.json();
      setErpCatalog(payload);
    } catch {
      // fallback silencioso para o catálogo estático
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    loadCatalog().then(() => {
      if (!isMounted) return;
    });
    return () => {
      isMounted = false;
    };
  }, [loadCatalog]);

  useEffect(() => {
    sessionStorage.setItem('cg-freeform-pane-widths-v1', JSON.stringify({ left: leftPanelWidth, right: rightPanelWidth }));
  }, [leftPanelWidth, rightPanelWidth]);

  useEffect(() => {
    if (!resizingPanel) return undefined;

    const handleMouseMove = (event) => {
      const rootRect = canvasRootRef.current?.getBoundingClientRect();
      if (!rootRect) return;

      if (resizingPanel === 'left') {
        const maxLeftWidth = Math.max(240, rootRect.width - rightPanelWidth - 320);
        const nextLeftWidth = Math.min(maxLeftWidth, Math.max(208, event.clientX - rootRect.left));
        setLeftPanelWidth(nextLeftWidth);
      }

      if (resizingPanel === 'right') {
        const maxRightWidth = Math.max(300, rootRect.width - leftPanelWidth - 320);
        const nextRightWidth = Math.min(maxRightWidth, Math.max(272, rootRect.right - event.clientX));
        setRightPanelWidth(nextRightWidth);
      }
    };

    const handleMouseUp = () => {
      setResizingPanel(null);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [leftPanelWidth, resizingPanel, rightPanelWidth]);

  // FIX: Multi-select transformer logic
  useEffect(() => {
    if (trRef.current) {
      // Find nodes for selected IDs
      const nodes = selectedIds
        .map(id => shapeRefs.current[id])
        .filter(Boolean);
        
      trRef.current.nodes(nodes);
      trRef.current.getLayer().batchDraw();
    }
  }, [selectedIds, elements]); // Re-run when elements change to update refs if needed

  // Register refs callback
  const handleRegisterRef = useCallback((id, node) => {
    if (node) {
      shapeRefs.current[id] = node;
    } else {
      delete shapeRefs.current[id];
    }
  }, []);

  // Add to history
  const addToHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newElements]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(history[historyIndex - 1]);
      setSelectedIds([]);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(history[historyIndex + 1]);
      setSelectedIds([]);
    }
  }, [history, historyIndex]);

  const gridSpacingMm = Math.max(1, gridSettings.spacingMm || DEFAULT_GRID_SETTINGS.spacingMm);
  const gridSpacingPt = gridSpacingMm * MM_TO_PT;
  const gridMajorEvery = Math.max(1, Math.round(10 / gridSpacingMm));

  const handleGridToggle = (checked) => {
    setGridSettings((prev) => ({
      ...prev,
      enabled: checked,
    }));
  };

  const handleGridSnapToggle = (checked) => {
    setGridSettings((prev) => ({
      ...prev,
      snap: checked,
    }));
  };

  const handleGridSpacingChange = (value) => {
    const spacingValue = Number(value);
    if (Number.isNaN(spacingValue)) return;
    const next = Math.min(20, Math.max(2, spacingValue));
    setGridSettings((prev) => ({
      ...prev,
      spacingMm: next,
    }));
  };

  // Add element
  const handleAddElement = (type) => {
    const defaults = DEFAULT_ELEMENT_PROPS[type];
    const newElement = {
      ...defaults,
      columns: Array.isArray(defaults?.columns) ? cloneDatasourceColumns(defaults.columns) : defaults?.columns,
      id: generateId(),
      x: 50 + Math.random() * 100,
      y: 50 + Math.random() * 100,
      zIndex: elements.length,
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedIds([newElement.id]); // Select newly created
  };

  const handleAddBankBlock = (variant = 'compact') => {
    const baseY = 520;
    const baseX = 52;
    const blockElements = variant === 'compact'
      ? [
          {
            id: generateId(),
            type: 'text',
            text: 'Dados Bancários',
            x: baseX,
            y: baseY,
            width: 180,
            height: 22,
            fontSize: 13,
            fontWeight: 'bold',
            zIndex: elements.length,
          },
          {
            id: generateId(),
            type: 'table',
            datasource: 'company_banks',
            columns: cloneDatasourceColumns(getTableDatasourceMeta('company_banks', tableDatasourceOptions)?.defaultColumns || []).slice(0, 4),
            presentationMode: 'list',
            bankDisposition: 'stacked',
            fontSize: 9,
            x: baseX,
            y: baseY + 30,
            width: 420,
            height: 140,
            zIndex: elements.length + 1,
          },
        ]
      : [
          {
            id: generateId(),
            type: 'text',
            text: 'Dados Bancários da Empresa',
            x: baseX,
            y: baseY,
            width: 240,
            height: 22,
            fontSize: 14,
            fontWeight: 'bold',
            zIndex: elements.length,
          },
          {
            id: generateId(),
            type: 'table',
            datasource: 'company_banks',
            columns: cloneDatasourceColumns(getTableDatasourceMeta('company_banks', tableDatasourceOptions)?.defaultColumns || []),
            presentationMode: 'table',
            bankDisposition: 'inline',
            fontSize: 9,
            x: baseX,
            y: baseY + 30,
            width: 500,
            height: 180,
            zIndex: elements.length + 1,
          },
        ];

    const newElements = [...elements, ...blockElements];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedIds(blockElements.map((element) => element.id));
  };

  // Update element
  const handleUpdateElement = useCallback((updatedElement) => {
    const newElements = elements.map(el => 
      el.id === updatedElement.id ? updatedElement : el
    );
    setElements(newElements);
    addToHistory(newElements);
  }, [addToHistory, elements]);

  // Delete element
  const handleDeleteElement = (id) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    addToHistory(newElements);
    setSelectedIds(prev => prev.filter(pid => pid !== id));
  };

  const selectedElement = elements.find(el => el.id === selectedIds[0]);
  const selectedElementValidation = selectedElement ? elementValidationMap[selectedElement.id] : null;

  const withReorderedElements = useCallback((updater) => {
    const ordered = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const nextOrdered = updater(ordered);
    const normalized = resequenceZIndexes(nextOrdered);
    setElements(normalized);
    addToHistory(normalized);
    return normalized;
  }, [addToHistory, elements]);

  const handleBringForward = useCallback((id) => {
    withReorderedElements((ordered) => {
      const index = ordered.findIndex((item) => item.id === id);
      if (index === -1 || index === ordered.length - 1) return ordered;
      const clone = [...ordered];
      [clone[index], clone[index + 1]] = [clone[index + 1], clone[index]];
      return clone;
    });
  }, [withReorderedElements]);

  const applyLayoutState = useCallback((layout, options = {}) => {
    const { pushHistory = true } = options;
    const normalizedElements = normalizeLayoutElements(layout?.elements || []);
    const nextZoom = Math.max(0.5, Math.min(2, roundTo2(Number(layout?.zoom) || 0.8)));
    const nextGrid = layout?.grid || layout?.gridSettings || {};

    setElements(normalizedElements);
    setZoom(nextZoom);
    setGridSettings({
      enabled: nextGrid.enabled ?? DEFAULT_GRID_SETTINGS.enabled,
      spacingMm: nextGrid.spacingMm ?? DEFAULT_GRID_SETTINGS.spacingMm,
      snap: nextGrid.snap ?? DEFAULT_GRID_SETTINGS.snap,
    });
    setSelectedIds([]);

    if (pushHistory) {
      addToHistory(normalizedElements);
    } else {
      setHistory([normalizedElements]);
      setHistoryIndex(0);
    }

    return normalizedElements;
  }, [addToHistory]);

  // Load initial layout
  useEffect(() => {
    if (initialLayout?.elements) {
      const draft = readTemplateDraft(templateId);
      const initialSignature = normalizeLayoutSignature(initialLayout);
      const draftSignature = draft?.layout?.elements ? normalizeLayoutSignature(draft.layout) : '';
      const hasMeaningfulDraftDiff = Boolean(draftSignature) && draftSignature !== initialSignature;
      const draftLayout = isStructuredPosLayout(initialLayout)
        ? null
        : (hasMeaningfulDraftDiff && shouldUseDraft(draft, templateUpdatedAt) ? draft?.layout : null);
      const sourceLayout = draftLayout?.elements ? draftLayout : initialLayout;
      applyLayoutState(sourceLayout, { pushHistory: false });
      setDraftBaselineSignature(draftLayout?.elements ? initialSignature : normalizeLayoutSignature(sourceLayout));

      if (isStructuredPosLayout(initialLayout) && draft?.layout?.elements) {
        clearTemplateDraft(templateId);
        restoredDraftToastRef.current.delete(templateId);
      } else if (draftLayout?.elements) {
        if (!restoredDraftToastRef.current.has(templateId)) {
          restoredDraftToastRef.current.add(templateId);
          toast.info('Rascunho local restaurado para este template.');
        }
      } else if (draft?.layout?.elements) {
        clearTemplateDraft(templateId);
        restoredDraftToastRef.current.delete(templateId);
      }
    }
  }, [initialLayout, templateId, templateUpdatedAt, applyLayoutState]);

  const handleSendBackward = useCallback((id) => {
    withReorderedElements((ordered) => {
      const index = ordered.findIndex((item) => item.id === id);
      if (index <= 0) return ordered;
      const clone = [...ordered];
      [clone[index], clone[index - 1]] = [clone[index - 1], clone[index]];
      return clone;
    });
  }, [withReorderedElements]);

  const handleBringToFront = useCallback((id) => {
    withReorderedElements((ordered) => {
      const target = ordered.find((item) => item.id === id);
      if (!target) return ordered;
      return [...ordered.filter((item) => item.id !== id), target];
    });
  }, [withReorderedElements]);

  const handleSendToBack = useCallback((id) => {
    withReorderedElements((ordered) => {
      const target = ordered.find((item) => item.id === id);
      if (!target) return ordered;
      return [target, ...ordered.filter((item) => item.id !== id)];
    });
  }, [withReorderedElements]);

  const normalizedElements = useMemo(() => normalizeLayoutElements(elements), [elements]);
  const currentCanvasWidth = initialLayout?.canvasWidth || A4_WIDTH;
  const currentCanvasHeight = useMemo(() => {
    const baseHeight = initialLayout?.canvasHeight || A4_HEIGHT;
    const bottomEdge = normalizedElements.reduce((max, element) => Math.max(max, (element?.y || 0) + (element?.height || 0)), 0);
    if (initialLayout?.autoHeight) {
      return Math.max(baseHeight, Math.ceil(bottomEdge + 40));
    }
    return baseHeight;
  }, [initialLayout?.autoHeight, initialLayout?.canvasHeight, normalizedElements]);
  const currentLayout = useMemo(() => ({
    canvasWidth: currentCanvasWidth,
    canvasHeight: currentCanvasHeight,
    isThermal: Boolean(initialLayout?.isThermal),
    autoHeight: Boolean(initialLayout?.autoHeight),
    meta: initialLayout?.meta || {},
    zoom,
    elements: normalizedElements,
    grid: {
      enabled: gridSettings.enabled,
      spacingMm: gridSpacingMm,
      majorEvery: gridMajorEvery,
      snap: gridSettings.snap,
    },
  }), [currentCanvasHeight, currentCanvasWidth, gridMajorEvery, gridSettings.enabled, gridSettings.snap, gridSpacingMm, initialLayout?.autoHeight, initialLayout?.isThermal, initialLayout?.meta, normalizedElements, zoom]);

  // Get current layout
  const getCurrentLayout = useCallback(() => currentLayout, [currentLayout]);

  useEffect(() => {
    if (!templateId || historyIndex < 0) return;
    if (normalizeLayoutSignature(currentLayout) === draftBaselineSignature) return;
    persistTemplateDraft(templateId, currentLayout);
  }, [templateId, currentLayout, historyIndex, draftBaselineSignature]);

  useEffect(() => {
    if (jsonDirty) return;
    setJsonEditorValue(JSON.stringify(currentLayout, null, 2));
  }, [currentLayout, jsonDirty]);

  const compareTemplateOptions = useMemo(
    () => availableTemplates.filter((template) => template.id !== templateId),
    [availableTemplates, templateId]
  );

  useEffect(() => {
    if (!compareTemplateOptions.length) {
      setCompareTemplateId('');
      return;
    }
    if (!compareTemplateId || !compareTemplateOptions.some((template) => template.id === compareTemplateId)) {
      setCompareTemplateId(compareTemplateOptions[0].id);
    }
  }, [compareTemplateId, compareTemplateOptions]);

  const compareTemplate = useMemo(
    () => compareTemplateOptions.find((template) => template.id === compareTemplateId) || null,
    [compareTemplateId, compareTemplateOptions]
  );
  const compareTemplateSelectValue = compareTemplateId || compareTemplateOptions[0]?.id || '__none__';

  const compareLayout = compareTemplate?.layout || compareTemplate?.freeform_layout || null;
  const compareElements = useMemo(() => compareLayout?.elements || [], [compareLayout]);
  const primarySelectedElementId = selectedIds[0] || '';
  const autoMatchedCompareElement = useMemo(
    () => (primarySelectedElementId ? compareElements.find((element) => element.id === primarySelectedElementId) || null : null),
    [compareElements, primarySelectedElementId]
  );

  useEffect(() => {
    if (autoMatchedCompareElement?.id) {
      setCompareElementId(autoMatchedCompareElement.id);
      return;
    }
    if (compareElements.length === 0) {
      setCompareElementId('');
      return;
    }
    if (!compareElementId || !compareElements.some((element) => element.id === compareElementId)) {
      setCompareElementId(compareElements[0].id);
    }
  }, [autoMatchedCompareElement, compareElementId, compareElements]);

  const resolvedCompareElement = useMemo(
    () => autoMatchedCompareElement || compareElements.find((element) => element.id === compareElementId) || null,
    [autoMatchedCompareElement, compareElementId, compareElements]
  );
  const compareElementSelectValue = compareElementId || compareElements[0]?.id || '__none__';

  const bulkReferencePlan = useMemo(() => {
    const compareById = new Map(compareElements.map((element) => [element.id, element]));
    const usedCompareIds = new Set();
    const matches = [];
    const unmatched = [];

    elements.forEach((element) => {
      const sameId = compareById.get(element.id);
      if (sameId && sameId.type === element.type) {
        usedCompareIds.add(sameId.id);
        matches.push({ current: element, reference: sameId, mode: 'id' });
        return;
      }

      const sameType = compareElements.find((candidate) => candidate.type === element.type && !usedCompareIds.has(candidate.id));
      if (sameType) {
        usedCompareIds.add(sameType.id);
        matches.push({ current: element, reference: sameType, mode: 'type' });
        return;
      }

      unmatched.push(element);
    });

    return {
      matches,
      unmatched,
      idMatches: matches.filter((item) => item.mode === 'id').length,
      typeMatches: matches.filter((item) => item.mode === 'type').length,
    };
  }, [compareElements, elements]);

  const handleApplyReferencePropertiesToSelected = useCallback(() => {
    if (!selectedElement) {
      toast.error('Seleccione primeiro um elemento do template actual');
      return;
    }
    if (!resolvedCompareElement) {
      toast.error('Seleccione um elemento de referência');
      return;
    }
    if (selectedElement.type !== resolvedCompareElement.type) {
      toast.error('Os dois elementos têm tipos diferentes. Escolha um elemento compatível.');
      return;
    }

    const nextElement = mergeElementPropertiesFromReference(selectedElement, resolvedCompareElement);

    handleUpdateElement(nextElement);
    toast.success(autoMatchedCompareElement ? 'Propriedades sincronizadas do elemento correspondente' : 'Propriedades aplicadas do elemento de referência');
  }, [selectedElement, resolvedCompareElement, autoMatchedCompareElement, handleUpdateElement]);

  const openBulkReferencePreview = useCallback(() => {
    if (!compareTemplate) {
      toast.error('Escolha primeiro um template de referência');
      return;
    }
    if (bulkReferencePlan.matches.length === 0) {
      toast.error('Nenhum elemento compatível encontrado para aplicar em massa');
      return;
    }
    setBulkReferenceDialogOpen(true);
  }, [bulkReferencePlan.matches.length, compareTemplate]);

  const handleApplyReferencePropertiesToAllCompatible = useCallback(() => {
    if (bulkReferencePlan.matches.length === 0) {
      toast.error('Nenhum elemento compatível encontrado para aplicar em massa');
      return;
    }

    const updatedElements = elements.map((element) => {
      const match = bulkReferencePlan.matches.find((item) => item.current.id === element.id);
      if (!match) return element;
      return mergeElementPropertiesFromReference(element, match.reference);
    });

    setElements(updatedElements);
    addToHistory(updatedElements);
    setBulkReferenceDialogOpen(false);
    toast.success(`Propriedades aplicadas em ${bulkReferencePlan.matches.length} elementos compatíveis`);
  }, [addToHistory, bulkReferencePlan.matches, elements]);


  const layoutDiffSummary = useMemo(
    () => (compareLayout ? buildLayoutDiffSummary(currentLayout, compareLayout) : null),
    [compareLayout, currentLayout]
  );

  const currentJsonLines = useMemo(() => JSON.stringify(currentLayout, null, 2).split('\n'), [currentLayout]);
  const compareJsonLines = useMemo(
    () => (compareLayout ? JSON.stringify(compareLayout, null, 2).split('\n') : []),
    [compareLayout]
  );

  // Save
  const handleInjectCatalogItem = useCallback((item) => {
    if (!item?.path) {
      toast.error('Entrada do catálogo inválida');
      return;
    }

    const nextElement = buildCatalogInjectedElement(item, selectedElement, tableDatasourceOptions);

    if (selectedElement) {
      const updatedElements = elements.map((element) => (
        element.id === selectedElement.id ? { ...nextElement, zIndex: element.zIndex || 0 } : element
      ));
      setElements(updatedElements);
      addToHistory(updatedElements);
      setSelectedIds([selectedElement.id]);
      toast.success(`Elemento seleccionado actualizado com ${item.label || item.path}`);
    } else {
      const updatedElements = resequenceZIndexes([...elements, { ...nextElement, zIndex: elements.length }]);
      setElements(updatedElements);
      addToHistory(updatedElements);
      setSelectedIds([nextElement.id]);
      toast.success(`Novo elemento inserido a partir de ${item.label || item.path}`);
    }

    setCatalogInsertDialogOpen(false);
  }, [addToHistory, elements, selectedElement]);

  const handleSave = async () => {
    if (!onSave) return;
    const layoutToSave = getCurrentLayout();
    try {
      await onSave(layoutToSave);
      setDraftBaselineSignature(normalizeLayoutSignature(layoutToSave));
      clearTemplateDraft(templateId);
      restoredDraftToastRef.current.delete(templateId);
    } catch {
      // o erro principal é tratado pelo caller
    }
  };

  // Preview
  const handlePreview = async () => {
    if (onPreview) {
      onPreview(getCurrentLayout());
    }
  };

  const handleCopyLayoutJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonEditorValue || JSON.stringify(currentLayout, null, 2));
      toast.success('Layout JSON copiado');
    } catch {
      toast.error('Não foi possível copiar o JSON');
    }
  };

  const handleFormatLayoutJson = () => {
    try {
      const parsed = JSON.parse(jsonEditorValue || '{}');
      setJsonEditorValue(JSON.stringify(parsed, null, 2));
      setJsonDirty(true);
      toast.success('JSON formatado');
    } catch {
      toast.error('JSON inválido para formatar');
    }
  };

  const normalizeImportedPatch = (parsed) => {
    if (Array.isArray(parsed)) {
      return { elements: parsed };
    }
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('O JSON deve ser um objecto ou uma lista de elementos');
    }

    const normalized = { ...parsed };
    if (normalized.gridSettings && !normalized.grid) {
      normalized.grid = normalized.gridSettings;
      delete normalized.gridSettings;
    }
    return normalized;
  };

  const handleApplyLayoutJson = (mode = 'merge') => {
    try {
      const parsed = normalizeImportedPatch(JSON.parse(jsonEditorValue || '{}'));
      const baseLayout = getCurrentLayout();
      let nextLayout;

      if (mode === 'merge') {
        nextLayout = deepMergeLayout(baseLayout, parsed);
        if (Array.isArray(parsed.elements)) {
          nextLayout.elements = mergeElementsByIdentity(baseLayout.elements, parsed.elements);
        }
      } else {
        nextLayout = deepMergeLayout(baseLayout, parsed);
      }

      applyLayoutState(nextLayout, { pushHistory: true });
      setJsonDirty(false);
      setJsonEditorValue(JSON.stringify(nextLayout, null, 2));
      toast.success(mode === 'merge' ? 'JSON aplicado ao layout actual' : 'Layout substituído via JSON');
    } catch (error) {
      toast.error(error?.message || 'JSON inválido');
    }
  };

  const handleResetJsonToCurrent = () => {
    setJsonEditorValue(JSON.stringify(getCurrentLayout(), null, 2));
    setJsonDirty(false);
    toast.success('JSON sincronizado com o layout actual');
  };

  // Click on empty canvas
  const handleStageClick = (e) => {
    if (Date.now() - stageInteractionLockRef.current < 180) {
      return;
    }
    const target = e.target;
    const isStage = target === target?.getStage();
    const isLayer = target?.getClassName?.() === 'Layer';
    if (isStage || isLayer) {
      setSelectedIds([]); // FIX: Clear selection
    }
  };

  const markStageInteractionLocked = useCallback(() => {
    stageInteractionLockRef.current = Date.now();
  }, []);

  const handleStageWheel = useCallback((e) => {
    if (!e.evt?.ctrlKey) return;
    e.evt.preventDefault();
    const direction = e.evt.deltaY > 0 ? -0.05 : 0.05;
    setZoom((prev) => Math.max(0.5, Math.min(2, roundTo2(prev + direction))));
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedIds.length > 0) {
        // Delete all selected
        const newElements = elements.filter(el => !selectedIds.includes(el.id));
        setElements(newElements);
        addToHistory(newElements);
        setSelectedIds([]);
      }
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z') {
          e.preventDefault();
          handleUndo();
        }
        if (e.key === 'y') {
          e.preventDefault();
          handleRedo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, elements, addToHistory, handleUndo, handleRedo]);

  const focusElementInCanvas = useCallback((id) => {
    const node = shapeRefs.current[id];
    const wrapper = stageScrollRef.current;
    if (!node || !wrapper) return;

    const nextLeft = Math.max((node.x() * zoom) - (wrapper.clientWidth / 2) + ((node.width?.() || 80) * zoom / 2), 0);
    const nextTop = Math.max((node.y() * zoom) - (wrapper.clientHeight / 2) + ((node.height?.() || 30) * zoom / 2), 0);
    wrapper.scrollTo({ left: nextLeft, top: nextTop, behavior: 'smooth' });
  }, [zoom]);

  const updateSelection = useCallback((id, isShift, shouldFocus = false) => {
    if (isShift) {
      setSelectedIds((prev) => {
        if (prev.includes(id)) {
          return prev.filter((pid) => pid !== id);
        }
        return [...prev, id];
      });
      if (shouldFocus) {
        setTimeout(() => focusElementInCanvas(id), 0);
      }
      return;
    }
    setSelectedIds([id]);
    if (shouldFocus) {
      setTimeout(() => focusElementInCanvas(id), 0);
    }
  }, [focusElementInCanvas]);

  // FIX: Multi-select logic
  const handleSelectElement = useCallback((id, event) => {
    const isShift = event?.evt?.shiftKey || event?.evt?.metaKey || event?.evt?.ctrlKey;

    if (event?.evt) {
      event.evt.cancelBubble = true;
    }

    markStageInteractionLocked();
    updateSelection(id, Boolean(isShift));
  }, [markStageInteractionLocked, updateSelection]);

  // Get first selected element for properties panel
  const filteredElementList = useMemo(() => {
    const query = elementSearch.trim().toLowerCase();
    return [...elements]
      .sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0))
      .filter((element) => {
        if (!query) return true;
        return [
          getElementDisplayLabel(element, variableOptions, tableDatasourceOptions),
          getElementTypeLabel(element.type),
          element.variablePath,
          element.id,
          `x:${Math.round(element.x)} y:${Math.round(element.y)}`,
        ].some((value) => String(value || '').toLowerCase().includes(query));
      });
  }, [elementSearch, elements]);

  const isLeftPanelNarrow = leftPanelWidth < 236;
  const isLeftPanelWide = leftPanelWidth > 300;
  const isRightPanelNarrow = rightPanelWidth < 320;

  const gridLines = useMemo(() => {
    if (!gridSettings.enabled) return [];
    const lines = [];
    const minorColor = '#e5e7eb';
    const majorColor = '#d1d5db';

    const totalWidth = currentLayout.canvasWidth;
    const totalHeight = currentLayout.canvasHeight;
    const step = gridSpacingPt; // Use calculated PT spacing

    const verticalCount = Math.ceil(totalWidth / step);
    for (let i = 0; i <= verticalCount; i += 1) {
      const x = Math.round(i * step * 100) / 100;
      const isMajor = i % gridMajorEvery === 0;
      lines.push(
        <Line
          key={`v-${i}`}
          points={[x, 0, x, totalHeight]}
          stroke={isMajor ? majorColor : minorColor}
          strokeWidth={isMajor ? 0.8 : 0.4}
        />
      );
    }

    const horizontalCount = Math.ceil(totalHeight / step);
    for (let i = 0; i <= horizontalCount; i += 1) {
      const y = Math.round(i * step * 100) / 100;
      const isMajor = i % gridMajorEvery === 0;
      lines.push(
        <Line
          key={`h-${i}`}
          points={[0, y, totalWidth, y]}
          stroke={isMajor ? majorColor : minorColor}
          strokeWidth={isMajor ? 0.8 : 0.4}
        />
      );
    }

    return lines;
  }, [gridSettings.enabled, gridSpacingPt, gridMajorEvery]);

  return (
    <div ref={canvasRootRef} className="flex h-full min-h-0 min-w-0 overflow-hidden" data-testid="freeform-canvas">
      {/* Tools Sidebar */}
      <div className="shrink-0 border-r bg-background flex min-h-0 flex-col" style={{ width: leftPanelWidth }} data-testid="freeform-left-panel">
        <div className="p-3 border-b">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Layers className="h-4 w-4" /> Elementos
          </h3>
        </div>
        
        <div className="p-2.5 space-y-1.5">
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddElement('text')}
            data-testid="add-text-element"
          >
            <Type className="h-4 w-4 mr-2" /> {isLeftPanelNarrow ? 'Texto' : 'Texto Estático'}
          </Button>
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddElement('variable')}
            data-testid="add-variable-element"
          >
            <Variable className="h-4 w-4 mr-2" /> {isLeftPanelNarrow ? 'Campo' : 'Campo Dinâmico'}
          </Button>
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddElement('image')}
            data-testid="add-image-element"
          >
            <Image className="h-4 w-4 mr-2" /> {isLeftPanelNarrow ? 'Imagem' : 'Imagem/Logo'}
          </Button>
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddElement('rect')}
            data-testid="add-rect-element"
          >
            <Square className="h-4 w-4 mr-2" /> {isLeftPanelNarrow ? 'Rect.' : 'Rectângulo'}
          </Button>
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddElement('line')}
            data-testid="add-line-element"
          >
            <Minus className="h-4 w-4 mr-2" /> Linha
          </Button>
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddElement('table')}
            data-testid="add-table-element"
          >
            <Table2 className="h-4 w-4 mr-2" /> {isLeftPanelNarrow ? 'Tabela' : 'Tabela Dinâmica'}
          </Button>
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddBankBlock('compact')}
            data-testid="add-banking-block-compact"
          >
            <Landmark className="h-4 w-4 mr-2" /> {isLeftPanelNarrow ? 'Banco Lista' : 'Bloco Bancário (Lista)'}
          </Button>
          <Button
            variant="outline"
            className={`w-full justify-start ${isLeftPanelNarrow ? 'h-8 px-2 text-xs' : ''}`}
            onClick={() => handleAddBankBlock('table')}
            data-testid="add-banking-block-table"
          >
            <LayoutGrid className="h-4 w-4 mr-2" /> {isLeftPanelNarrow ? 'Banco Tabela' : 'Bloco Bancário (Tabela)'}
          </Button>
        </div>

        <Separator />

        {/* Element list */}
        <div className="flex-1 overflow-auto">
          <div className="p-3">
            <h4 className="text-xs font-semibold text-muted-foreground mb-2">
              ELEMENTOS ({elements.length})
            </h4>
            <Input
              value={elementSearch}
              onChange={(event) => setElementSearch(event.target.value)}
              placeholder={isLeftPanelNarrow ? 'Nome, variável, ID...' : 'Selecionar manualmente por nome, variável, ID...'}
              className="mb-3 h-8 text-xs"
              data-testid="element-selector-search"
            />
            <div className="mb-3 rounded-lg border bg-muted/40 p-2 text-[11px] text-muted-foreground" data-testid="element-selector-help">
              {isLeftPanelNarrow
                ? 'Use esta lista para seleccionar manualmente itens do layout.'
                : 'Use esta lista para seleccionar manualmente cada item do layout, mesmo quando existirem elementos sobrepostos no canvas.'}
            </div>
            {invalidBindingCount > 0 && (
              <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-2 text-[11px] text-red-700" data-testid="invalid-binding-summary-panel">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <div>
                    <p className="font-semibold">{invalidBindingCount} ligação(ões) ERP inválida(s)</p>
                    <p>Os elementos marcados a vermelho precisam de um path/datasource válido.</p>
                  </div>
                </div>
              </div>
            )}
            {elements.length === 0 ? (
              <p key="element-list-empty" className="text-xs text-muted-foreground">
                Adicione elementos usando os botões acima
              </p>
            ) : (
              <div className="space-y-1">
                {filteredElementList.map((el, index) => {
                  const itemValidation = elementValidationMap[el.id];
                  return (
                    <div
                      key={el.id}
                      className={`
                        p-2 rounded text-xs cursor-pointer border flex items-start gap-2
                        ${selectedIds.includes(el.id) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
                        ${itemValidation?.invalid ? 'border-red-300' : ''}
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        updateSelection(el.id, Boolean(e.shiftKey), true);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        updateSelection(el.id, false, true);
                      }}
                      data-testid={`element-list-item-${el.id}`}
                    >
                      {el.type === 'text' && <Type className="h-3 w-3" />}
                      {el.type === 'variable' && <Variable className="h-3 w-3" />}
                      {el.type === 'image' && <Image className="h-3 w-3" />}
                      {el.type === 'rect' && <Square className="h-3 w-3" />}
                      {el.type === 'line' && <Minus className="h-3 w-3" />}
                      {el.type === 'table' && <Table2 className="h-3 w-3" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`truncate font-medium ${isLeftPanelWide ? 'text-xs' : 'text-[11px]'}`} data-testid={`element-list-item-label-${el.id}`}>
                            {index + 1}. {getElementDisplayLabel(el, variableOptions, tableDatasourceOptions)}
                          </span>
                          <span className="text-[10px] opacity-80">z:{el.zIndex || 0}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[10px] opacity-80">
                          <span>{getElementTypeLabel(el.type)}</span>
                          {!isLeftPanelNarrow && <span>X:{Math.round(el.x)}</span>}
                          {!isLeftPanelNarrow && <span>Y:{Math.round(el.y)}</span>}
                          <span>{Math.round(el.width)}×{Math.round(el.height)}</span>
                          {itemValidation?.invalid && <span className="font-semibold text-red-600" data-testid={`element-list-item-warning-${el.id}`}>ERP inválido</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filteredElementList.length === 0 && (
                  <div key="element-selector-empty" className="rounded-lg border border-dashed p-3 text-[11px] text-muted-foreground" data-testid="element-selector-empty-state">
                    Nenhum item encontrado para a pesquisa actual.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`w-1.5 shrink-0 cursor-col-resize bg-border/60 transition-colors hover:bg-primary/40 ${resizingPanel === 'left' ? 'bg-primary/40' : ''}`}
        onMouseDown={() => setResizingPanel('left')}
        data-testid="freeform-left-panel-resizer"
      />

      {/* Canvas Area */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="min-h-[56px] border-b bg-background px-4 py-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="px-2 py-1 rounded border text-xs text-slate-600"
              data-testid="selected-count-indicator"
            >
              Selecionados: {selectedIds.length}
            </div>
            {invalidBindingCount > 0 && (
              <div className="px-2 py-1 rounded border border-red-300 bg-red-50 text-xs text-red-700" data-testid="invalid-binding-toolbar-indicator">
                Inválidos ERP: {invalidBindingCount}
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              title="Desfazer (Ctrl+Z)"
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              title="Refazer (Ctrl+Y)"
            >
              <Redo2 className="h-4 w-4" />
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
              title="Reduzir"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setZoom(Math.min(2, zoom + 0.1))}
              title="Ampliar"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2" data-testid="gridlines-controls">
              <LayoutGrid className="h-4 w-4 text-slate-500" />
              <Switch
                checked={gridSettings.enabled}
                onCheckedChange={handleGridToggle}
                data-testid="gridlines-toggle"
              />
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-600">Espaço</span>
                <Input
                  type="number"
                  min={2}
                  max={20}
                  value={gridSpacingMm}
                  onChange={(e) => handleGridSpacingChange(e.target.value)}
                  className="h-7 w-16 text-xs"
                  data-testid="gridlines-spacing-input"
                />
                <span className="text-[10px] text-slate-500">mm</span>
              </div>
              <div className="flex items-center gap-1">
                <Switch
                  checked={gridSettings.snap}
                  onCheckedChange={handleGridSnapToggle}
                  data-testid="gridlines-snap-toggle"
                />
                <span className="text-xs text-slate-600">Snap</span>
                <span className="text-[10px] text-slate-500">Alt p/ desativar</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => { setCatalogInsertDialogOpen(true); loadCatalog(); }} data-testid="open-erp-catalog-insert-btn">
              <Variable className="h-4 w-4 mr-2" /> Catálogo ERP
            </Button>
            <Button variant="outline" onClick={() => setDiffDialogOpen(true)} data-testid="open-layout-diff-btn">
              Comparar Template
            </Button>
            <Button variant="outline" onClick={handlePreview} data-testid="preview-btn">
              <Eye className="h-4 w-4 mr-2" /> Pré-visualizar PDF
            </Button>
            <Button onClick={handleSave} data-testid="save-layout-btn">
              <Save className="h-4 w-4 mr-2" /> Guardar Layout
            </Button>
          </div>
          </div>
        </div>

        {/* Canvas */}
        <div 
          ref={stageScrollRef}
          className="flex-1 overflow-auto bg-gray-200"
          style={{ padding: CANVAS_PADDING }}
          data-testid="template-canvas-stage-wrapper"
        >
          <div
            style={{
              width: currentLayout.canvasWidth * zoom,
              height: currentLayout.canvasHeight * zoom,
              margin: '0 auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              background: '#fff',
            }}
          >
            <Stage
              ref={stageRef}
              width={currentLayout.canvasWidth * zoom}
              height={currentLayout.canvasHeight * zoom}
              scaleX={zoom}
              scaleY={zoom}
              onClick={handleStageClick}
              onTap={handleStageClick}
              onWheel={handleStageWheel}
            >
              <Layer>
                {/* A4 Background */}
                <Rect
                  x={0}
                  y={0}
                  width={currentLayout.canvasWidth}
                  height={currentLayout.canvasHeight}
                  fill="#ffffff"
                  listening={false}
                />
              </Layer>
              {gridSettings.enabled && (
                <Layer listening={false}>
                  {gridLines}
                </Layer>
              )}
              <Layer>
                {/* Elements sorted by zIndex */}
                {[...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((element) => (
                  <CanvasElement
                    key={element.id}
                    element={element}
                    isSelected={selectedIds.includes(element.id)}
                    onSelect={handleSelectElement}
                    onChange={handleUpdateElement}
                    onDelete={handleDeleteElement}
                    gridSettings={gridSettings}
                    gridSpacingPt={gridSpacingPt}
                    onRegisterRef={handleRegisterRef}
                    onInteractionLock={markStageInteractionLocked}
                    variableOptions={variableOptions}
                    tableDatasources={tableDatasourceOptions}
                    validationState={elementValidationMap[element.id]}
                  />
                ))}
                
                {/* FIX: Transformer at Layer Level */}
                <Transformer
                  key="canvas-transformer"
                  ref={trRef}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < 20 || newBox.height < 10) {
                      return oldBox;
                    }
                    if (!(gridSettings.enabled && gridSettings.snap)) {
                      return newBox;
                    }
                    return {
                      ...newBox,
                      x: snapToGrid(newBox.x, gridSpacingPt),
                      y: snapToGrid(newBox.y, gridSpacingPt),
                      width: Math.max(20, snapToGrid(newBox.width, gridSpacingPt)),
                      height: Math.max(10, snapToGrid(newBox.height, gridSpacingPt)),
                    };
                  }}
                  rotateEnabled={false}
                  // Keep ratio only if 1 image selected and locked
                  keepRatio={
                    selectedIds.length === 1 && 
                    elements.find(e => e.id === selectedIds[0])?.type === 'image' && 
                    elements.find(e => e.id === selectedIds[0])?.lockAspectRatio
                  }
                />
              </Layer>
            </Stage>
          </div>
        </div>
      </div>

      <div
        className={`w-1.5 shrink-0 cursor-col-resize bg-border/60 transition-colors hover:bg-primary/40 ${resizingPanel === 'right' ? 'bg-primary/40' : ''}`}
        onMouseDown={() => setResizingPanel('right')}
        data-testid="freeform-right-panel-resizer"
      />

      {/* Properties / JSON Panel */}
      <div className="shrink-0 border-l bg-background min-h-0" style={{ width: rightPanelWidth }} data-testid="freeform-side-panel">
        <Tabs value={sidePanelTab} onValueChange={setSidePanelTab} className="h-full flex flex-col">
          <div className="p-3 border-b">
            <TabsList className="grid h-8 w-full grid-cols-2" data-testid="freeform-side-panel-tabs">
              <TabsTrigger value="properties" className="px-2 text-xs" data-testid="freeform-properties-tab">Props</TabsTrigger>
              <TabsTrigger value="json" className="px-2 text-xs" data-testid="freeform-json-tab">Layout JSON</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="properties" className="mt-0 flex-1 overflow-hidden">
            <div className="flex h-full flex-col overflow-hidden">
              <div className="flex-1 overflow-hidden">
                <PropertiesPanel
                  element={selectedElement}
                  onUpdate={handleUpdateElement}
                  onDelete={handleDeleteElement}
                  onBringForward={handleBringForward}
                  onSendBackward={handleSendBackward}
                  onBringToFront={handleBringToFront}
                  onSendToBack={handleSendToBack}
                  panelWidth={rightPanelWidth}
                  variableOptions={variableOptions}
                  tableDatasources={tableDatasourceOptions}
                  validationState={selectedElementValidation}
                />
              </div>

              <div className="border-t p-2.5 space-y-2" data-testid="reference-properties-sync-panel">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{isRightPanelNarrow ? 'Aplicar props da referência' : 'Aplicar propriedades da referência'}</p>
                  <p className="text-[11px] text-muted-foreground">{isRightPanelNarrow ? 'Copia propriedades e preserva o conteúdo actual.' : 'Copia propriedades do elemento de referência e preserva o conteúdo actual.'}</p>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <Label className="mb-1 block text-[11px] text-muted-foreground">{isRightPanelNarrow ? 'Template ref.' : 'Template de referência'}</Label>
                  <Select value={compareTemplateSelectValue} onValueChange={(value) => value !== '__none__' && setCompareTemplateId(value)}>
                    <SelectTrigger data-testid="reference-template-selector-inline">
                      <SelectValue placeholder="Escolher template de referência" />
                    </SelectTrigger>
                    <SelectContent>
                      {compareTemplateOptions.length > 0 ? compareTemplateOptions.map((template) => (
                        <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                      )) : <SelectItem value="__none__">Sem templates de referência</SelectItem>}
                    </SelectContent>
                  </Select>
                  </div>

                  <div>
                    <Label className="mb-1 block text-[11px] text-muted-foreground">{isRightPanelNarrow ? 'Elemento ref.' : 'Elemento de referência'}</Label>
                  <Select value={compareElementSelectValue} onValueChange={(value) => value !== '__none__' && setCompareElementId(value)}>
                    <SelectTrigger data-testid="reference-element-selector-inline">
                      <SelectValue placeholder="Escolher elemento de referência" />
                    </SelectTrigger>
                    <SelectContent>
                      {compareElements.length > 0 ? compareElements.map((element) => (
                        <SelectItem key={element.id} value={element.id}>
                          {getElementDisplayLabel(element, variableOptions, tableDatasourceOptions)}
                        </SelectItem>
                      )) : <SelectItem value="__none__">Sem elementos comparáveis</SelectItem>}
                    </SelectContent>
                  </Select>
                  </div>
                </div>
                  <p className="mt-1 text-[11px] text-muted-foreground" data-testid="reference-element-match-note">
                    {autoMatchedCompareElement ? 'Match automático por id.' : (isRightPanelNarrow ? 'Sem match por id; escolha manual.' : 'Sem correspondência por id; use a escolha manual.')}
                  </p>

                <div className={`grid gap-2 ${rightPanelWidth > 380 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <Button className="h-8 px-2 text-[11px]" onClick={handleApplyReferencePropertiesToSelected} disabled={!selectedElement || !resolvedCompareElement} data-testid="apply-reference-properties-inline-btn">
                    {isRightPanelNarrow ? 'Aplicar ao seleccionado' : 'Aplicar propriedades ao elemento seleccionado'}
                  </Button>
                  <Button variant="outline" className="h-8 px-2 text-[11px]" onClick={openBulkReferencePreview} disabled={!compareTemplate || bulkReferencePlan.matches.length === 0} data-testid="apply-reference-properties-all-inline-btn">
                    {isRightPanelNarrow ? 'Aplicar a todos' : 'Aplicar a todos os elementos compatíveis'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="json" className="mt-0 flex-1 overflow-hidden">
            <div className="flex h-full flex-col">
              <div className="border-b px-3 py-3 space-y-2" data-testid="layout-json-summary-header">
                <h3 className="font-semibold text-sm">Layout JSON (Resumo)</h3>
                <p className="text-xs text-muted-foreground">
                  Pode copiar, colar e aplicar apenas os dados necessários. O botão aplicar faz <strong>merge parcial</strong> sobre o layout actual e, quando enviar `elements`, tenta actualizar por `id`.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopyLayoutJson} data-testid="layout-json-copy-btn">Copiar</Button>
                  <Button variant="outline" size="sm" onClick={handleFormatLayoutJson} data-testid="layout-json-format-btn">Formatar</Button>
                  <Button variant="outline" size="sm" onClick={handleResetJsonToCurrent} data-testid="layout-json-reset-btn">Recarregar actual</Button>
                  <Button size="sm" onClick={() => handleApplyLayoutJson('merge')} data-testid="layout-json-apply-merge-btn">Aplicar JSON</Button>
                </div>
              </div>

              <div className="flex-1 overflow-hidden p-3">
                <Textarea
                  value={jsonEditorValue}
                  onChange={(event) => {
                    setJsonEditorValue(event.target.value);
                    setJsonDirty(true);
                  }}
                  className="h-full min-h-full resize-none font-mono text-[11px] leading-5"
                  placeholder='Cole aqui um objecto JSON completo ou parcial, por exemplo {"elements": [...]} ou {"grid": {"snap": true}}'
                  data-testid="layout-json-editor-textarea"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={catalogInsertDialogOpen} onOpenChange={setCatalogInsertDialogOpen}>
        <DialogContent className="max-w-4xl" data-testid="erp-catalog-insert-dialog">
          <DialogHeader>
            <DialogTitle>Inserir do Catálogo ERP</DialogTitle>
            <DialogDescription>
              {selectedElement
                ? `O elemento seleccionado (${getElementDisplayLabel(selectedElement, variableOptions, tableDatasourceOptions)}) será substituído pelo item escolhido.`
                : 'Sem selecção activa: o item escolhido será inserido automaticamente no canvas.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-slate-50 px-3 py-2" data-testid="erp-catalog-adapter-mode-bar">
              <p className="text-[11px] text-muted-foreground">Catálogo do editor a consumir o contrato canónico do backend.</p>
              <Badge variant="outline" data-testid="erp-catalog-adapter-source-badge">{erpCatalog?.catalog_source === 'canonical_backend' ? 'Contrato backend' : 'Fallback local'}</Badge>
            </div>
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_180px_auto]" data-testid="erp-catalog-insert-filters">
              <Input
                value={catalogSearch}
                onChange={(event) => setCatalogSearch(event.target.value)}
                placeholder="Pesquisar por path, nome ou grupo"
                data-testid="erp-catalog-insert-search"
              />
              <Select value={catalogModuleFilter} onValueChange={setCatalogModuleFilter}>
                <SelectTrigger data-testid="erp-catalog-insert-module-filter">
                  <SelectValue placeholder="Módulo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos módulos</SelectItem>
                  {catalogModuleOptions.map((module) => (
                    <SelectItem key={module} value={module}>{module}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={catalogDocTypeFilter} onValueChange={setCatalogDocTypeFilter}>
                <SelectTrigger data-testid="erp-catalog-insert-doc-type-filter">
                  <SelectValue placeholder="Doc type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos doc types</SelectItem>
                  {catalogDocTypeOptions.map((docType) => (
                    <SelectItem key={docType} value={docType}>{docType}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadCatalog} data-testid="erp-catalog-insert-refresh-btn">Actualizar</Button>
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 text-[11px] text-muted-foreground" data-testid="erp-catalog-insert-mode-note">
              Clique num item para {selectedElement ? 'substituir o elemento activo' : 'inserir um novo elemento'}. Tabelas usam datasource; campos usam variável/path.
            </div>

            <ScrollArea className="h-[420px] rounded-xl border" data-testid="erp-catalog-insert-scroll-area">
              <div className="space-y-2 p-3">
                {filteredCatalogItems.length > 0 ? filteredCatalogItems.map((item) => (
                  <button
                    type="button"
                    key={`${item.kind}-${item.path}`}
                    className="w-full rounded-xl border bg-white p-3 text-left transition-colors hover:border-primary hover:bg-primary/5"
                    onClick={() => handleInjectCatalogItem(item)}
                    data-testid={`erp-catalog-insert-item-${item.path}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-slate-900">{item.label || item.path}</span>
                          <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-600">{item.kind || 'campo'}</span>
                        </div>
                        <p className="mt-1 break-all font-mono text-[11px] text-slate-600">{item.path}</p>
                        <p className="mt-1 text-[11px] text-slate-500">{item.group || 'ERP'}{item.doc_types?.length ? ` • ${item.doc_types.join(', ')}` : ''}</p>
                        <div className="mt-2">
                          <CatalogContractBadges item={item} testIdBase={`freeform-catalog-item-${item.path.replace(/[^a-zA-Z0-9]+/g, '-')}`} />
                        </div>
                      </div>
                      <span className="rounded-full bg-slate-900 px-2 py-1 text-[10px] font-medium text-white">
                        {selectedElement ? 'Substituir' : 'Inserir'}
                      </span>
                    </div>
                  </button>
                )) : (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="erp-catalog-insert-empty-state">
                    Nenhum item do catálogo corresponde aos filtros actuais.
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={diffDialogOpen} onOpenChange={setDiffDialogOpen}>
        <DialogContent className="max-w-7xl" data-testid="layout-diff-dialog">
          <DialogHeader>
            <DialogTitle>Diff visual / JSON entre templates</DialogTitle>
            <DialogDescription>
              Compare rapidamente o template actual com outro template do sistema, em modo lado a lado, overlay e JSON completo com diferenças.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]" data-testid="layout-diff-controls">
              <div>
                <Label className="mb-2 block text-xs text-muted-foreground">Template de referência para comparação</Label>
                <Select value={compareTemplateSelectValue} onValueChange={(value) => value !== '__none__' && setCompareTemplateId(value)}>
                  <SelectTrigger data-testid="layout-diff-template-selector">
                    <SelectValue placeholder="Escolher template de referência" />
                  </SelectTrigger>
                  <SelectContent>
                    {compareTemplateOptions.length > 0 ? compareTemplateOptions.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    )) : <SelectItem value="__none__">Sem templates de referência</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Tabs value={diffViewMode} onValueChange={setDiffViewMode}>
                  <TabsList data-testid="layout-diff-view-tabs">
                    <TabsTrigger value="side-by-side" data-testid="layout-diff-tab-side-by-side">Lado a lado</TabsTrigger>
                    <TabsTrigger value="overlay" data-testid="layout-diff-tab-overlay">Overlay</TabsTrigger>
                    <TabsTrigger value="json" data-testid="layout-diff-tab-json">JSON</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]" data-testid="layout-diff-apply-reference-controls">
              <div>
                <Label className="mb-2 block text-xs text-muted-foreground">Elemento actual seleccionado</Label>
                <div className="rounded-md border px-3 py-2 text-sm" data-testid="layout-diff-current-element-label">
                  {selectedElement ? getElementDisplayLabel(selectedElement, variableOptions, tableDatasourceOptions) : 'Seleccione um elemento no editor'}
                </div>
              </div>
              <div>
                <Label className="mb-2 block text-xs text-muted-foreground">Elemento da referência</Label>
                <Select value={compareElementSelectValue} onValueChange={(value) => value !== '__none__' && setCompareElementId(value)}>
                  <SelectTrigger data-testid="layout-diff-reference-element-selector">
                    <SelectValue placeholder="Escolher elemento de referência" />
                  </SelectTrigger>
                  <SelectContent>
                    {compareElements.length > 0 ? compareElements.map((element) => (
                      <SelectItem key={element.id} value={element.id}>
                        {getElementDisplayLabel(element, variableOptions, tableDatasourceOptions)}
                      </SelectItem>
                    )) : <SelectItem value="__none__">Sem elementos comparáveis</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {autoMatchedCompareElement ? 'Auto-match por id activo.' : 'Sem auto-match; escolha manual disponível.'}
                </p>
              </div>
              <div className="flex items-end">
                <div className="grid gap-2">
                  <Button onClick={handleApplyReferencePropertiesToSelected} disabled={!selectedElement || !resolvedCompareElement} data-testid="layout-diff-apply-reference-btn">
                    Aplicar propriedades
                  </Button>
                  <Button variant="outline" onClick={openBulkReferencePreview} disabled={!compareTemplate || bulkReferencePlan.matches.length === 0} data-testid="layout-diff-apply-reference-all-btn">
                    Aplicar a todos
                  </Button>
                </div>
              </div>
            </div>

            {layoutDiffSummary && (
              <div className="grid gap-3 md:grid-cols-4" data-testid="layout-diff-summary-grid">
                <div className="rounded-lg border p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Zoom/Grid</p>
                  <p className="mt-2 font-medium">{layoutDiffSummary.zoomChanged || layoutDiffSummary.gridChanged ? 'Diferenças detectadas' : 'Sem diferenças'}</p>
                </div>
                <div className="rounded-lg border p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Elementos alterados</p>
                  <p className="mt-2 font-medium" data-testid="layout-diff-changed-count">{layoutDiffSummary.changedElements.length}</p>
                </div>
                <div className="rounded-lg border p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Elementos extra no comparado</p>
                  <p className="mt-2 font-medium">{layoutDiffSummary.addedElements.length}</p>
                </div>
                <div className="rounded-lg border p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Elementos ausentes no comparado</p>
                  <p className="mt-2 font-medium">{layoutDiffSummary.removedElements.length}</p>
                </div>
              </div>
            )}

            {compareLayout && diffViewMode === 'side-by-side' && (
              <div className="grid gap-4 lg:grid-cols-2" data-testid="layout-diff-side-by-side-view">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Actual: este template</p>
                  <StaticLayoutPreview layout={currentLayout} tone="current" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Referência: {compareTemplate?.name}</p>
                  <StaticLayoutPreview layout={compareLayout} tone="compare" />
                </div>
              </div>
            )}

            {compareLayout && diffViewMode === 'overlay' && (
              <div className="space-y-2" data-testid="layout-diff-overlay-view">
                <p className="text-sm font-medium">Overlay visual</p>
                <div className="overflow-auto rounded-xl border bg-slate-100 p-3">
                  <div className="relative origin-top-left bg-white shadow-sm" style={{ width: `${currentLayout.canvasWidth * 0.42}px`, height: `${currentLayout.canvasHeight * 0.42}px` }}>
                    <div className="absolute inset-0 opacity-80">
                      <StaticLayoutPreview layout={currentLayout} tone="current" framed={false} scale={0.42} />
                    </div>
                    <div className="absolute inset-0 opacity-55 mix-blend-multiply">
                      <StaticLayoutPreview layout={compareLayout} tone="compare" framed={false} scale={0.42} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {compareLayout && diffViewMode === 'json' && (
              <div className="grid gap-4 lg:grid-cols-2" data-testid="layout-diff-json-view">
                {[['Actual', currentJsonLines, compareJsonLines], ['Referência', compareJsonLines, currentJsonLines]].map(([title, lines, otherLines]) => (
                  <div key={title} className="space-y-2">
                    <p className="text-sm font-medium">{title}</p>
                    <div className="max-h-[420px] overflow-auto rounded-lg border bg-slate-950 p-3 text-[11px] leading-5 text-slate-100">
                      {lines.map((line, index) => {
                        const changed = line !== (otherLines[index] || '');
                        return (
                          <div
                            key={`${title}-${index}`}
                            className={changed ? 'bg-amber-500/15 text-amber-200' : 'text-slate-100'}
                            data-testid={changed ? `layout-diff-json-changed-${title}-${index}` : undefined}
                          >
                            {line || ' '}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkReferenceDialogOpen} onOpenChange={setBulkReferenceDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="bulk-reference-preview-dialog">
          <DialogHeader>
            <DialogTitle>Pré-resumo da aplicação em massa</DialogTitle>
            <DialogDescription>
              Primeiro tentamos correspondência por <strong>id</strong>; quando não houver, aplicamos por <strong>type</strong>. O conteúdo do elemento actual é preservado.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 md:grid-cols-4" data-testid="bulk-reference-summary-grid">
            <div className="rounded-lg border p-3 text-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Compatíveis totais</p>
              <p className="mt-2 font-medium">{bulkReferencePlan.matches.length}</p>
            </div>
            <div className="rounded-lg border p-3 text-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Match por id</p>
              <p className="mt-2 font-medium">{bulkReferencePlan.idMatches}</p>
            </div>
            <div className="rounded-lg border p-3 text-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Match por type</p>
              <p className="mt-2 font-medium">{bulkReferencePlan.typeMatches}</p>
            </div>
            <div className="rounded-lg border p-3 text-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Sem correspondência</p>
              <p className="mt-2 font-medium">{bulkReferencePlan.unmatched.length}</p>
            </div>
          </div>

          <div className="max-h-[320px] overflow-auto rounded-lg border" data-testid="bulk-reference-preview-list">
            <div className="divide-y">
              {bulkReferencePlan.matches.slice(0, 40).map((item) => (
                <div key={item.current.id} className="grid gap-2 px-3 py-2 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] text-sm">
                  <span className="truncate">{getElementDisplayLabel(item.current, variableOptions, tableDatasourceOptions)}</span>
                  <span className="text-xs text-muted-foreground">{item.mode === 'id' ? 'id' : 'type'}</span>
                  <span className="truncate text-muted-foreground">{getElementDisplayLabel(item.reference, variableOptions, tableDatasourceOptions)}</span>
                </div>
              ))}
              {bulkReferencePlan.matches.length > 40 && (
                <div key="bulk-reference-overflow-note" className="px-3 py-2 text-xs text-muted-foreground">
                  ... e mais {bulkReferencePlan.matches.length - 40} correspondências
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkReferenceDialogOpen(false)} data-testid="bulk-reference-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={handleApplyReferencePropertiesToAllCompatible} data-testid="bulk-reference-confirm-btn">
              Aplicar em massa
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
